/* Popsy Halloween Costume Idea Generator
 * Adaptive question journey
 * Guaranteed-result scoring
 * Wardrobe-aware outfit guidance
 * Personalised stories
 * Costume-specific Popsy AI links
 */

(function (root) {
  'use strict';

  const VERSION = 'adaptive-match-2.1.0';
  const QUESTIONNAIRE_VERSION = '2026-10-24-v7';
  const CONSENT_VERSION = '2026-10-20-v1';

  const opt = (value, label, description = '') => ({
    value,
    label,
    description
  });

  const esc = value =>
    String(value == null ? '' : value).replace(
      /[&<>"']/g,
      character =>
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;'
        })[character]
    );

  const uuid = () =>
    root.crypto?.randomUUID
      ? root.crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
          /[xy]/g,
          character => {
            const number = Math.floor(Math.random() * 16);

            return (
              character === 'x'
                ? number
                : (number & 3) | 8
            ).toString(16);
          }
        );

  const random = () =>
    root.crypto?.getRandomValues
      ? root.crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296
      : Math.random();

  const humanList = values => {
    const list = values.filter(Boolean);

    if (!list.length) return '';
    if (list.length === 1) return list[0];
    if (list.length === 2) return `${list[0]} and ${list[1]}`;

    return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
  };

  const REGIONS = [
    opt('north_east', 'North East England'),
    opt('north_west', 'North West England'),
    opt('yorkshire_humber', 'Yorkshire and the Humber'),
    opt('east_midlands', 'East Midlands'),
    opt('west_midlands', 'West Midlands'),
    opt('east_of_england', 'East of England'),
    opt('london', 'London'),
    opt('south_east', 'South East England'),
    opt('south_west', 'South West England'),
    opt('scotland', 'Scotland'),
    opt('wales', 'Wales'),
    opt('northern_ireland', 'Northern Ireland'),
    opt('outside_uk', 'Outside the UK'),
    opt('prefer_not_say', 'Prefer not to say')
  ];

  const WARDROBE_PLANS = [
    opt('mostly_owned', 'Mostly clothes I already own'),
    opt('mix_owned_new', 'A mixture of things I own and new things'),
    opt('mostly_new', 'Mostly new things'),
    opt('not_sure', 'I have not decided'),
    opt('prefer_not_say', 'Prefer not to say')
  ];

  const OWNED_DEFS = {
    dark_dress: {
      label: 'a dark dress',
      wardrobes: ['dark_dress'],
      keywords: ['dark dress', 'black dress']
    },

    colourful_dress: {
      label: 'a colourful or patterned dress',
      wardrobes: ['colourful_dress'],
      keywords: ['colourful dress', 'patterned dress']
    },

    favourite_dress: {
      label: 'a favourite dress',
      wardrobes: ['dark_dress', 'colourful_dress'],
      keywords: ['dress']
    },

    dark_basics: {
      label: 'dark everyday clothes',
      wardrobes: ['dark_basics', 'casual_basics'],
      keywords: ['dark clothes', 'black clothes', 'dark everyday']
    },

    colourful_clothes: {
      label: 'colourful everyday clothes',
      wardrobes: ['colourful_dress', 'casual_basics'],
      keywords: ['colourful clothes', 'bright clothes']
    },

    white_shirt: {
      label: 'a white or pale shirt',
      wardrobes: ['shirt_trousers', 'pale_outfit'],
      keywords: ['white shirt', 'pale shirt', 'roomy white shirt']
    },

    shirt_trousers: {
      label: 'a shirt and trousers',
      wardrobes: ['shirt_trousers'],
      keywords: ['shirt and trousers', 'shirt', 'trousers']
    },

    dark_trousers: {
      label: 'dark trousers',
      wardrobes: ['shirt_trousers', 'dark_basics'],
      keywords: ['dark trousers', 'black trousers']
    },

    jeans_dungarees: {
      label: 'jeans or dungarees',
      wardrobes: ['denim'],
      keywords: ['jeans', 'dungarees', 'denim']
    },

    cardigan_jacket: {
      label: 'a cardigan or jacket',
      wardrobes: ['layers'],
      keywords: ['cardigan', 'jacket', 'blazer']
    },

    coat: {
      label: 'a coat',
      wardrobes: ['layers'],
      keywords: ['coat']
    },

    pale_clothes: {
      label: 'white or pale clothes',
      wardrobes: ['pale_outfit'],
      keywords: ['pale outfit', 'white clothes', 'pale clothes']
    },

    pyjamas: {
      label: 'casual clothes or pyjamas',
      wardrobes: ['casual_basics'],
      keywords: ['pyjamas', 'casual clothes']
    },

    cape: {
      label: 'a cape',
      wardrobes: [],
      keywords: ['cape']
    },

    boots: {
      label: 'boots',
      wardrobes: [],
      keywords: ['boots', 'ankle boots']
    },

    trainers: {
      label: 'trainers',
      wardrobes: [],
      keywords: ['trainers']
    },

    hat: {
      label: 'a hat',
      wardrobes: [],
      keywords: ['hat', 'beret', 'pointed hat']
    },

    waistcoat: {
      label: 'a waistcoat',
      wardrobes: [],
      keywords: ['waistcoat', 'vest']
    },

    scarf: {
      label: 'a scarf',
      wardrobes: [],
      keywords: ['scarf']
    },

    bag: {
      label: 'a small bag or satchel',
      wardrobes: [],
      keywords: ['bag', 'satchel']
    },

    apron: {
      label: 'an apron',
      wardrobes: [],
      keywords: ['apron']
    },

    glasses: {
      label: 'glasses',
      wardrobes: [],
      keywords: ['glasses']
    },

    none: {
      label: 'Nothing useful yet',
      wardrobes: [],
      keywords: []
    }
  };

  const ownedOpt = id =>
    opt(
      id,
      OWNED_DEFS[id]?.label || id
    );

  const QUESTION_BANK = {
    audience: {
      key: 'audience',
      type: 'single',
      title: 'Who are we finding a Halloween idea for?',
      hint: 'This changes the questions and costume ideas you’ll see.',
      options: [
        opt('woman', 'Woman'),
        opt('man', 'Man'),
        opt('child', 'Child'),
        opt('couple', 'Two people'),
        opt('family', 'Family or group')
      ]
    },

    child_type: {
      key: 'child_type',
      type: 'single',
      title: 'Who are we dressing?',
      hint: 'This mainly helps us choose the most suitable character illustration.',
      options: [
        opt('girl', 'Girl'),
        opt('boy', 'Boy'),
        opt('any', 'No preference')
      ]
    },

    couple_type: {
      key: 'couple_type',
      type: 'single',
      title: 'Who are the two people?',
      hint: 'We’ll try to find two ideas that work together.',
      options: [
        opt('women', 'Two women'),
        opt('mixed', 'A woman and a man'),
        opt('men', 'Two men'),
        opt('any', 'No preference')
      ]
    },

    family_composition: {
      key: 'family_composition',
      type: 'composition',
      title: 'Who is in your Halloween crew?',
      hint: 'Make the four groups add up to your total number of people.'
    },

    occasion: {
      key: 'occasion',
      type: 'single',
      title: 'Where are you planning to wear it?',
      hint: 'We’ll favour ideas that make sense for the day you actually have planned.',
      options: [
        opt('party', 'Halloween party'),
        opt('work', 'Work'),
        opt('school', 'School'),
        opt('trick_or_treat', 'Trick or treating'),
        opt('home', 'At home or with family'),
        opt('any', 'Not decided yet')
      ]
    },

    worlds: {
      key: 'worlds',
      type: 'multi',
      max: 2,
      title: 'Which Halloween worlds sound most fun?',
      hint: 'Choose one or two. This is where your adventure starts.',
      options: [
        opt(
          'classic',
          'Classic Halloween',
          'Witches, ghosts, pumpkins and familiar favourites'
        ),
        opt(
          'gothic',
          'Haunted castle',
          'Vampires, moonlight, mysterious guests and old houses'
        ),
        opt(
          'pirate',
          'Pirate adventure',
          'Treasure, maps, captains and explorers'
        ),
        opt(
          'magic',
          'Magic school',
          'Witches, potions, books, stars and strange lessons'
        ),
        opt(
          'storybook',
          'Enchanted storybook',
          'Fairies, royalty, magical animals and curious travellers'
        ),
        opt(
          'mystery',
          'Mystery mission',
          'Detectives, spies, clues and ghost hunters'
        ),
        opt(
          'science',
          'Mad laboratory',
          'Scientists, potion makers, inventors and experiments'
        ),
        opt(
          'colourful',
          'Colourful chaos',
          'Rainbows, art, pumpkins and bright magic'
        ),
        opt(
          'funny',
          'Something a bit silly',
          'Playful characters and costumes that make people smile'
        )
      ]
    },

    moods: {
      key: 'moods',
      type: 'multi',
      max: 3,
      title: 'How would you like your costume to feel?',
      hint: 'Choose up to three. You don’t have to fit neatly into one box.',
      options: [
        opt('magical', 'Magical'),
        opt('spooky', 'Spooky'),
        opt('funny', 'Funny'),
        opt('colourful', 'Colourful'),
        opt('storybook', 'Storybook'),
        opt('gentle', 'Sweet or gentle'),
        opt('dramatic', 'Dramatic'),
        opt('classic', 'Classic Halloween')
      ]
    },

    owned_woman: {
      key: 'owned_woman',
      type: 'multi',
      max: 7,
      title: 'What do you already have that we could build around?',
      hint: 'Tick anything useful. We’ll try to make those pieces do some of the work.',
      options: [
        ownedOpt('dark_dress'),
        ownedOpt('colourful_dress'),
        ownedOpt('dark_basics'),
        ownedOpt('jeans_dungarees'),
        ownedOpt('cardigan_jacket'),
        ownedOpt('coat'),
        ownedOpt('cape'),
        ownedOpt('boots'),
        ownedOpt('trainers'),
        ownedOpt('hat'),
        ownedOpt('scarf'),
        ownedOpt('bag'),
        ownedOpt('none')
      ]
    },

    owned_man: {
      key: 'owned_man',
      type: 'multi',
      max: 7,
      title: 'What do you already have that could become part of the costume?',
      hint: 'A cape is useful, but a white shirt and ordinary dark clothes can go a surprisingly long way.',
      options: [
        ownedOpt('white_shirt'),
        ownedOpt('dark_trousers'),
        ownedOpt('dark_basics'),
        ownedOpt('jeans_dungarees'),
        ownedOpt('cardigan_jacket'),
        ownedOpt('coat'),
        ownedOpt('waistcoat'),
        ownedOpt('cape'),
        ownedOpt('boots'),
        ownedOpt('hat'),
        ownedOpt('scarf'),
        ownedOpt('glasses'),
        ownedOpt('none')
      ]
    },

    owned_girl: {
      key: 'owned_girl',
      type: 'multi',
      max: 6,
      title: 'What could we use from their wardrobe?',
      hint: 'Comfortable familiar clothes are a very good place to start.',
      options: [
        ownedOpt('favourite_dress'),
        ownedOpt('colourful_clothes'),
        ownedOpt('dark_basics'),
        ownedOpt('jeans_dungarees'),
        ownedOpt('cardigan_jacket'),
        ownedOpt('coat'),
        ownedOpt('cape'),
        ownedOpt('boots'),
        ownedOpt('trainers'),
        ownedOpt('hat'),
        ownedOpt('none')
      ]
    },

    owned_boy: {
      key: 'owned_boy',
      type: 'multi',
      max: 6,
      title: 'What could we use from their wardrobe?',
      hint: 'We’ll build the character around familiar clothes wherever we can.',
      options: [
        ownedOpt('white_shirt'),
        ownedOpt('shirt_trousers'),
        ownedOpt('dark_basics'),
        ownedOpt('colourful_clothes'),
        ownedOpt('jeans_dungarees'),
        ownedOpt('cardigan_jacket'),
        ownedOpt('coat'),
        ownedOpt('cape'),
        ownedOpt('boots'),
        ownedOpt('trainers'),
        ownedOpt('hat'),
        ownedOpt('none')
      ]
    },

    owned_child: {
      key: 'owned_child',
      type: 'multi',
      max: 6,
      title: 'What could we use from their wardrobe?',
      hint: 'Choose anything that sounds useful.',
      options: [
        ownedOpt('favourite_dress'),
        ownedOpt('white_shirt'),
        ownedOpt('dark_basics'),
        ownedOpt('colourful_clothes'),
        ownedOpt('jeans_dungarees'),
        ownedOpt('cardigan_jacket'),
        ownedOpt('cape'),
        ownedOpt('boots'),
        ownedOpt('trainers'),
        ownedOpt('hat'),
        ownedOpt('none')
      ]
    },

    owned_group: {
      key: 'owned_group',
      type: 'multi',
      max: 8,
      title: 'What useful costume basics do you already have between you?',
      hint: 'Think of this as the group dressing-up cupboard.',
      options: [
        ownedOpt('dark_dress'),
        ownedOpt('colourful_dress'),
        ownedOpt('white_shirt'),
        ownedOpt('dark_trousers'),
        ownedOpt('dark_basics'),
        ownedOpt('colourful_clothes'),
        ownedOpt('jeans_dungarees'),
        ownedOpt('cardigan_jacket'),
        ownedOpt('coat'),
        ownedOpt('cape'),
        ownedOpt('boots'),
        ownedOpt('hat'),
        ownedOpt('none')
      ]
    },

    classic_detail: {
      key: 'classic_detail',
      type: 'single',
      title: 'Which classic Halloween direction catches your eye?',
      hint: 'Go with the one you’d be happiest to find at the end.',
      options: [
        opt('witch', 'Witch'),
        opt('vampire', 'Vampire'),
        opt('ghost', 'Ghost'),
        opt('skeleton', 'Skeleton'),
        opt('pumpkin', 'Pumpkin'),
        opt('devil', 'Devil'),
        opt('surprise', 'Surprise me within the theme')
      ]
    },

    gothic_detail: {
      key: 'gothic_detail',
      type: 'single',
      title: 'What would you rather find inside the haunted castle?',
      hint: 'This nudges your result without completely deciding it.',
      options: [
        opt('vampire', 'A vampire'),
        opt('ghost', 'A friendly ghost'),
        opt('dark_magic', 'Dark magic'),
        opt('mystery', 'A mysterious guest'),
        opt('royal', 'Gothic royalty'),
        opt('surprise', 'Open the mysterious door')
      ]
    },

    adventure_detail: {
      key: 'adventure_detail',
      type: 'single',
      title: 'Pick your adventure.',
      hint: 'Where should the costume idea take you?',
      options: [
        opt('pirate', 'Pirate ship'),
        opt('explorer', 'Expedition'),
        opt('mapmaker', 'Magical map'),
        opt('spy', 'Secret mission'),
        opt('hunter', 'Ghost hunt'),
        opt('surprise', 'Somewhere unexpected')
      ]
    },

    magic_detail: {
      key: 'magic_detail',
      type: 'single',
      title: 'Which bit of magic would you choose first?',
      hint: 'There are no exams at the end of this lesson.',
      options: [
        opt('witch', 'A witchy spell'),
        opt('potion', 'Potion making'),
        opt('school', 'Magic school'),
        opt('stars', 'Moon and stars'),
        opt('book', 'An enchanted book'),
        opt('surprise', 'Let the spell decide')
      ]
    },

    story_detail: {
      key: 'story_detail',
      type: 'single',
      title: 'Which storybook door would you open?',
      hint: 'Pick the chapter that sounds most like your sort of Halloween.',
      options: [
        opt('fairy', 'Fairy garden'),
        opt('royal', 'Royal court'),
        opt('animal', 'Magical animals'),
        opt('library', 'Enchanted library'),
        opt('traveller', 'A strange journey'),
        opt('surprise', 'Turn a random page')
      ]
    },

    mystery_detail: {
      key: 'mystery_detail',
      type: 'single',
      title: 'Your first clue arrives. What kind of mystery is it?',
      hint: 'This helps us decide who you might become.',
      options: [
        opt('detective', 'Detective case'),
        opt('spy', 'Secret mission'),
        opt('guest', 'Mysterious invitation'),
        opt('hunter', 'Ghost investigation'),
        opt('science', 'Strange experiment'),
        opt('surprise', 'The clue is unreadable')
      ]
    },

    science_detail: {
      key: 'science_detail',
      type: 'single',
      title: 'What would you rather discover in the laboratory?',
      hint: 'A sensible answer is not required.',
      options: [
        opt('scientist', 'A strange experiment'),
        opt('potion', 'A potion'),
        opt('inventor', 'A peculiar invention'),
        opt('maker', 'Something handmade'),
        opt('explorer', 'A new discovery'),
        opt('surprise', 'Press the mysterious button')
      ]
    },

    colour_detail: {
      key: 'colour_detail',
      type: 'single',
      title: 'Where should all that colour go?',
      hint: 'Choose the direction that sounds most fun.',
      options: [
        opt('rainbow', 'Rainbow character'),
        opt('witch', 'Colourful witch'),
        opt('artist', 'Artist or maker'),
        opt('pumpkin', 'Pumpkin party'),
        opt('fairy', 'Fairy magic'),
        opt('surprise', 'Use every colour')
      ]
    },

    funny_detail: {
      key: 'funny_detail',
      type: 'single',
      title: 'What sort of funny are we talking about?',
      hint: 'A little silliness can still make a very good costume.',
      options: [
        opt('devil', 'Cheeky Halloween'),
        opt('zombie', 'Silly spooky'),
        opt('animal', 'Animal character'),
        opt('mystery', 'Odd mystery'),
        opt('colourful', 'Colourful chaos'),
        opt('surprise', 'Make me laugh')
      ]
    },

    woman_finish: {
      key: 'woman_finish',
      type: 'multi',
      max: 3,
      title: 'Which finishing touches would you actually wear?',
      hint: 'Choose up to three, or choose none of these.',
      options: [
        opt('cape', 'Cape'),
        opt('hat', 'Hat'),
        opt('boots', 'Boots'),
        opt('jewellery', 'Jewellery'),
        opt('wand', 'Wand'),
        opt('wings', 'Wings'),
        opt('mask', 'Mask'),
        opt('none', 'None of these')
      ]
    },

    man_finish: {
      key: 'man_finish',
      type: 'multi',
      max: 3,
      title: 'Which finishing touches would you be happy to add?',
      hint: 'Choose up to three, or keep it very simple.',
      options: [
        opt('cape', 'Cape'),
        opt('hat', 'Hat'),
        opt('waistcoat', 'Waistcoat'),
        opt('boots', 'Boots'),
        opt('scarf', 'Scarf'),
        opt('glasses', 'Glasses'),
        opt('prop', 'Simple prop'),
        opt('none', 'Keep it very simple')
      ]
    },

    child_finish: {
      key: 'child_finish',
      type: 'multi',
      max: 3,
      title: 'Which extras would they enjoy?',
      hint: 'Choose things they would actually be comfortable wearing or carrying.',
      options: [
        opt('cape', 'Cape'),
        opt('hat', 'Hat'),
        opt('wand', 'Wand'),
        opt('wings', 'Wings'),
        opt('mask', 'Mask'),
        opt('bag', 'Little bag'),
        opt('book', 'Book or notebook'),
        opt('none', 'Keep it simple')
      ]
    },

    group_finish: {
      key: 'group_finish',
      type: 'single',
      title: 'How should the group fit together?',
      hint: 'This changes how strongly we try to coordinate the characters.',
      options: [
        opt('matching', 'Clearly matching'),
        opt('same_world', 'Same world, different characters'),
        opt('mixed', 'Different ideas that still work together'),
        opt('surprise', 'Let the generator decide')
      ]
    },

    practical: {
      key: 'practical',
      type: 'multi',
      max: 3,
      title: 'Anything the costume needs to cope with?',
      hint: 'Choose up to three practical things that matter.',
      options: [
        opt('warm', 'Needs warm layers'),
        opt('outdoors', 'Mostly outdoors'),
        opt('indoors', 'Mostly indoors'),
        opt('sit_down', 'Needs to be easy to sit in'),
        opt('hands_free', 'Prefer hands free'),
        opt('work_school', 'Needs to stay work or school friendly'),
        opt('none', 'Nothing in particular')
      ]
    },

    effort: {
      key: 'effort',
      type: 'single',
      title: 'How much preparation do you fancy?',
      hint: 'This is about real-life effort, not how impressive the finished costume looks.',
      options: [
        opt(
          'quick',
          'Keep it quick',
          'Everyday clothes and one or two finishing touches'
        ),
        opt(
          'creative',
          'Happy to get creative',
          'A bit of making, layering or accessorising is fine'
        ),
        opt('any', 'Either is fine')
      ]
    },

    budget: {
      key: 'budget',
      type: 'single',
      title: 'How much are you planning to spend on anything extra?',
      hint: 'We won’t ask a second affordability question later. This answer is enough.',
      options: [
        opt('zero', 'Nothing extra'),
        opt('one_to_10', '£1 to £10'),
        opt('eleven_to_20', '£11 to £20'),
        opt('twentyone_to_40', '£21 to £40'),
        opt('over_40', 'More than £40'),
        opt('not_sure', 'Not decided'),
        opt('prefer_not_say', 'Prefer not to say')
      ]
    },

    wildcard_a: {
      key: 'wildcard_a',
      type: 'single',
      title: 'Don’t think too hard. Pick one.',
      hint: 'This only gives the final result a tiny nudge.',
      options: [
        opt('moon', 'Moon'),
        opt('key', 'Key'),
        opt('feather', 'Feather'),
        opt('map', 'Map'),
        opt('pumpkin', 'Pumpkin'),
        opt('star', 'Star')
      ]
    },

    wildcard_b: {
      key: 'wildcard_b',
      type: 'single',
      title: 'A door appears. Where does it lead?',
      hint: 'There is no sensible answer.',
      options: [
        opt('library', 'Old library'),
        opt('castle', 'Castle'),
        opt('ship', 'Pirate ship'),
        opt('lab', 'Laboratory'),
        opt('forest', 'Enchanted forest'),
        opt('party', 'Halloween party')
      ]
    },

    wildcard_c: {
      key: 'wildcard_c',
      type: 'single',
      title: 'One object is waiting for you. Which do you take?',
      hint: 'This is the slightly mysterious part of the quiz.',
      options: [
        opt('book', 'Book'),
        opt('cape', 'Cape'),
        opt('hat', 'Hat'),
        opt('wand', 'Wand'),
        opt('map', 'Map'),
        opt('lantern', 'Lantern')
      ]
    }
  };

  const DEFAULTS = {
    audience: '',
    child_type: '',
    couple_type: '',

    group_size: 3,
    group_women_count: 1,
    group_men_count: 1,
    group_girls_count: 1,
    group_boys_count: 0,

    occasion: '',
    worlds: [],
    moods: [],

    owned_woman: [],
    owned_man: [],
    owned_girl: [],
    owned_boy: [],
    owned_child: [],
    owned_group: [],

    woman_finish: [],
    man_finish: [],
    child_finish: [],
    group_finish: '',

    practical: [],

    effort: '',
    budget: ''
  };

  function costumeNumber(record) {
    return Number(
      String(record?.costume_id || '').replace(/^H/, '')
    );
  }

  function sketchPresentation(record) {
    const id = costumeNumber(record);

    if (id >= 1 && id <= 100) {
      return 'female';
    }

    if (id >= 101 && id <= 150) {
      return 'male';
    }

    if (id >= 151 && id <= 200) {
      return id % 2 === 1
        ? 'female'
        : 'male';
    }

    return 'neutral';
  }

  function normaliseAnswers(input = {}) {
    return {
      ...DEFAULTS,
      ...input,

      worlds:
        Array.isArray(input.worlds)
          ? input.worlds
          : [],

      moods:
        Array.isArray(input.moods)
          ? input.moods
          : [],

      owned_woman:
        Array.isArray(input.owned_woman)
          ? input.owned_woman
          : [],

      owned_man:
        Array.isArray(input.owned_man)
          ? input.owned_man
          : [],

      owned_girl:
        Array.isArray(input.owned_girl)
          ? input.owned_girl
          : [],

      owned_boy:
        Array.isArray(input.owned_boy)
          ? input.owned_boy
          : [],

      owned_child:
        Array.isArray(input.owned_child)
          ? input.owned_child
          : [],

      owned_group:
        Array.isArray(input.owned_group)
          ? input.owned_group
          : [],

      woman_finish:
        Array.isArray(input.woman_finish)
          ? input.woman_finish
          : [],

      man_finish:
        Array.isArray(input.man_finish)
          ? input.man_finish
          : [],

      child_finish:
        Array.isArray(input.child_finish)
          ? input.child_finish
          : [],

      practical:
        Array.isArray(input.practical)
          ? input.practical
          : [],

      group_size:
        Math.max(
          3,
          Math.min(
            6,
            Number(input.group_size) || 3
          )
        ),

      group_women_count:
        Math.max(
          0,
          Number(input.group_women_count) || 0
        ),

      group_men_count:
        Math.max(
          0,
          Number(input.group_men_count) || 0
        ),

      group_girls_count:
        Math.max(
          0,
          Number(input.group_girls_count) || 0
        ),

      group_boys_count:
        Math.max(
          0,
          Number(input.group_boys_count) || 0
        )
    };
  }

  function questionLabel(key, value) {
    const question =
      QUESTION_BANK[key];

    return (
      question?.options?.find(
        option =>
          option.value === value
      )?.label ||
      value ||
      ''
    );
  }

  function labelsFor(key, values) {
    return (values || []).map(
      value =>
        questionLabel(
          key,
          value
        )
    );
  }

  function ownedQuestionKey(answers) {
    if (
      answers.audience === 'woman'
    ) {
      return 'owned_woman';
    }

    if (
      answers.audience === 'man'
    ) {
      return 'owned_man';
    }

    if (
      answers.audience === 'child'
    ) {
      if (
        answers.child_type === 'girl'
      ) {
        return 'owned_girl';
      }

      if (
        answers.child_type === 'boy'
      ) {
        return 'owned_boy';
      }

      return 'owned_child';
    }

    return 'owned_group';
  }

  function finishQuestionKey(answers) {
    if (
      answers.audience === 'woman'
    ) {
      return 'woman_finish';
    }

    if (
      answers.audience === 'man'
    ) {
      return 'man_finish';
    }

    if (
      answers.audience === 'child'
    ) {
      return 'child_finish';
    }

    return 'group_finish';
  }

  function branchQuestionKey(answers, variant) {
    const worlds =
      answers.worlds || [];

    if (!worlds.length) {
      return [
        'classic_detail',
        'magic_detail',
        'mystery_detail'
      ][variant % 3];
    }

    const world =
      worlds[
        variant %
        worlds.length
      ];

    const map = {
      classic:
        'classic_detail',

      gothic:
        'gothic_detail',

      pirate:
        'adventure_detail',

      magic:
        'magic_detail',

      storybook:
        'story_detail',

      mystery:
        'mystery_detail',

      science:
        'science_detail',

      colourful:
        'colour_detail',

      funny:
        'funny_detail'
    };

    return (
      map[world] ||
      'classic_detail'
    );
  }

  function wildcardQuestionKey(variant) {
    return [
      'wildcard_a',
      'wildcard_b',
      'wildcard_c'
    ][variant % 3];
  }

  function journeyFor(input, variant) {
    const answers =
      normaliseAnswers(input);

    const journey = [
      'audience'
    ];

    if (!answers.audience) {
      return journey;
    }

    if (
      answers.audience === 'child'
    ) {
      journey.push(
        'child_type'
      );
    }

    if (
      answers.audience === 'couple'
    ) {
      journey.push(
        'couple_type'
      );
    }

    if (
      answers.audience === 'family'
    ) {
      journey.push(
        'family_composition'
      );
    }

    journey.push(
      'occasion',
      'worlds',
      'moods',
      ownedQuestionKey(answers),
      branchQuestionKey(
        answers,
        variant
      ),
      finishQuestionKey(answers),
      'practical',
      'effort',
      'budget',
      wildcardQuestionKey(
        variant
      )
    );

    return journey;
  }

  const ARCHETYPE_PATTERNS = {
    witch:
      /witch|coven/i,

    vampire:
      /vampire/i,

    ghost:
      /ghost|haunt/i,

    skeleton:
      /skeleton/i,

    pumpkin:
      /pumpkin/i,

    pirate:
      /pirate|treasure|captain/i,

    magic_school:
      /magic school|school teacher|potion|wizard|spell|magic/i,

    fairy:
      /fairy|spirit|enchanted|rainbow/i,

    royal:
      /queen|king|royal|crown|princess|prince/i,

    mystery:
      /mystery|detective|spy|clue|secret mission|ghost hunter|invitation/i,

    science:
      /scientist|laboratory|experiment|potion maker|invent|maker/i,

    animal:
      /animal|pet|cat|dog|fox|owl|bear|keeper/i,

    celestial:
      /moon|star|sky|celestial/i,

    zombie:
      /zombie/i,

    devil:
      /devil|horn|trident/i,

    storybook:
      /storybook|fairytale|library|book|dream|traveller|mapmaker/i,

    adventure:
      /adventure|travel|map|explorer|pirate|captain/i,

    art:
      /artist|art|colour|rainbow|paint/i
  };

  const WORLD_ARCHETYPES = {
    classic: [
      'witch',
      'vampire',
      'ghost',
      'skeleton',
      'pumpkin',
      'devil',
      'zombie'
    ],

    gothic: [
      'vampire',
      'ghost',
      'mystery',
      'royal',
      'celestial'
    ],

    pirate: [
      'pirate',
      'adventure',
      'storybook'
    ],

    magic: [
      'witch',
      'magic_school',
      'celestial',
      'science'
    ],

    storybook: [
      'storybook',
      'fairy',
      'royal',
      'animal',
      'adventure'
    ],

    mystery: [
      'mystery',
      'ghost',
      'science'
    ],

    science: [
      'science',
      'magic_school',
      'art'
    ],

    colourful: [
      'art',
      'fairy',
      'pumpkin',
      'witch'
    ],

    funny: [
      'devil',
      'zombie',
      'animal',
      'art',
      'mystery'
    ]
  };

  const DETAIL_ARCHETYPES = {
    witch:
      ['witch'],

    vampire:
      ['vampire'],

    ghost:
      ['ghost'],

    skeleton:
      ['skeleton'],

    pumpkin:
      ['pumpkin'],

    devil:
      ['devil'],

    dark_magic:
      [
        'witch',
        'celestial',
        'magic_school'
      ],

    mystery:
      ['mystery'],

    royal:
      ['royal'],

    pirate:
      ['pirate'],

    explorer:
      ['adventure'],

    mapmaker:
      [
        'adventure',
        'storybook'
      ],

    spy:
      ['mystery'],

    hunter:
      [
        'mystery',
        'ghost'
      ],

    potion:
      [
        'science',
        'magic_school'
      ],

    school:
      ['magic_school'],

    stars:
      ['celestial'],

    book:
      [
        'storybook',
        'magic_school'
      ],

    fairy:
      ['fairy'],

    animal:
      ['animal'],

    library:
      ['storybook'],

    traveller:
      [
        'adventure',
        'storybook'
      ],

    detective:
      ['mystery'],

    guest:
      ['mystery'],

    science:
      ['science'],

    scientist:
      ['science'],

    inventor:
      ['science'],

    maker:
      [
        'science',
        'art'
      ],

    rainbow:
      [
        'art',
        'fairy'
      ],

    artist:
      ['art'],

    zombie:
      ['zombie'],

    colourful:
      [
        'art',
        'fairy'
      ]
  };

  const WILDCARD_ARCHETYPES = {
    moon:
      [
        'celestial',
        'vampire'
      ],

    key:
      [
        'mystery',
        'storybook'
      ],

    feather:
      [
        'pirate',
        'fairy'
      ],

    map:
      [
        'adventure',
        'pirate'
      ],

    pumpkin:
      ['pumpkin'],

    star:
      [
        'celestial',
        'witch'
      ],

    library:
      [
        'storybook',
        'mystery'
      ],

    castle:
      [
        'vampire',
        'royal',
        'ghost'
      ],

    ship:
      ['pirate'],

    lab:
      ['science'],

    forest:
      [
        'fairy',
        'animal'
      ],

    party:
      [
        'classic',
        'art'
      ],

    book:
      [
        'storybook',
        'magic_school'
      ],

    cape:
      [
        'vampire',
        'witch'
      ],

    hat:
      [
        'witch',
        'pirate'
      ],

    wand:
      [
        'witch',
        'fairy'
      ],

    lantern:
      [
        'ghost',
        'adventure'
      ]
  };

  function costumeText(record) {
    return [
      record?.display_result?.costume_title,
      record?.display_result?.short_description,
      record?.display_result?.story,
      record?.matching?.wardrobe_tip,
      ...(record?.matching?.theme_tags || [])
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  function recordArchetypes(record) {
    const text =
      costumeText(record);

    const found = [];

    for (
      const [
        name,
        pattern
      ] of Object.entries(
        ARCHETYPE_PATTERNS
      )
    ) {
      if (
        pattern.test(text)
      ) {
        found.push(name);
      }
    }

    return found;
  }

  function roleForSingle(answers) {
    if (
      answers.audience === 'woman'
    ) {
      return 'female';
    }

    if (
      answers.audience === 'man'
    ) {
      return 'male';
    }

    if (
      answers.audience === 'child'
    ) {
      if (
        answers.child_type === 'girl'
      ) {
        return 'girl';
      }

      if (
        answers.child_type === 'boy'
      ) {
        return 'boy';
      }

      return 'child';
    }

    return 'any';
  }

  function rolesForGroup(answers) {
    if (
      answers.audience === 'couple'
    ) {
      if (
        answers.couple_type === 'women'
      ) {
        return [
          'female',
          'female'
        ];
      }

      if (
        answers.couple_type === 'mixed'
      ) {
        return [
          'female',
          'male'
        ];
      }

      if (
        answers.couple_type === 'men'
      ) {
        return [
          'male',
          'male'
        ];
      }

      return [
        'any',
        'any'
      ];
    }

    if (
      answers.audience === 'family'
    ) {
      const roles = [];

      for (
        let i = 0;
        i <
        answers.group_women_count;
        i++
      ) {
        roles.push('female');
      }

      for (
        let i = 0;
        i <
        answers.group_men_count;
        i++
      ) {
        roles.push('male');
      }

      for (
        let i = 0;
        i <
        answers.group_girls_count;
        i++
      ) {
        roles.push('girl');
      }

      for (
        let i = 0;
        i <
        answers.group_boys_count;
        i++
      ) {
        roles.push('boy');
      }

      return roles;
    }

    return [
      roleForSingle(answers)
    ];
  }

  function isChildRole(role) {
    return [
      'girl',
      'boy',
      'child'
    ].includes(role);
  }

  function roleCompatible(
    record,
    role,
    relaxed = false
  ) {
    if (
      record.active === false
    ) {
      return false;
    }

    const presentation =
      sketchPresentation(
        record
      );

    if (
      role === 'any'
    ) {
      return true;
    }

    if (
      role === 'female'
    ) {
      return (
        presentation ===
        'female'
      );
    }

    if (
      role === 'male'
    ) {
      return (
        presentation ===
        'male'
      );
    }

    if (
      role === 'girl'
    ) {
      if (
        record.matching
          ?.child_adaptable !==
        true
      ) {
        return false;
      }

      return (
        relaxed ||
        presentation ===
          'female'
      );
    }

    if (
      role === 'boy'
    ) {
      if (
        record.matching
          ?.child_adaptable !==
        true
      ) {
        return false;
      }

      return (
        relaxed ||
        presentation ===
          'male'
      );
    }

    if (
      role === 'child'
    ) {
      return (
        record.matching
          ?.child_adaptable ===
        true
      );
    }

    return true;
  }

  function ownedIdsForAnswers(answers) {
    const key =
      ownedQuestionKey(
        answers
      );

    return (
      answers[key] || []
    ).filter(
      id =>
        id !== 'none'
    );
  }

  function finishValuesForAnswers(answers) {
    const key =
      finishQuestionKey(
        answers
      );

    if (
      key === 'group_finish'
    ) {
      return [];
    }

    return (
      answers[key] || []
    ).filter(
      value =>
        value !== 'none'
    );
  }

  function ownedCompatible(record, ownedId) {
    const definition =
      OWNED_DEFS[
        ownedId
      ];

    if (!definition) {
      return false;
    }

    const wardrobes =
      record.matching
        ?.wardrobes ||
      [];

    if (
      definition.wardrobes
        .some(
          wardrobe =>
            wardrobes.includes(
              wardrobe
            )
        )
    ) {
      return true;
    }

    const text =
      costumeText(record);

    return (
      definition.keywords
        .some(
          keyword =>
            text.includes(
              keyword
            )
        )
    );
  }

  function finishCompatible(
    record,
    finish
  ) {
    const text =
      costumeText(record);

    const patterns = {
      cape:
        /cape/,

      hat:
        /hat|beret|witch|pirate/,

      boots:
        /boot/,

      jewellery:
        /jewel|necklace|medallion|royal/,

      wand:
        /wand|witch|fairy|magic/,

      wings:
        /wing|fairy/,

      mask:
        /mask|mystery/,

      waistcoat:
        /waistcoat|vest|pirate|vampire/,

      scarf:
        /scarf/,

      glasses:
        /glasses|scientist|teacher/,

      prop:
        /map|wand|book|key|flask|lantern|binocular|trident|notebook|torch|detector/,

      bag:
        /bag|satchel/,

      book:
        /book|notebook|library/
    };

    return (
      patterns[finish]
        ?.test(text) ||
      false
    );
  }

  function branchAnswer(answers) {
    const keys = [
      'classic_detail',
      'gothic_detail',
      'adventure_detail',
      'magic_detail',
      'story_detail',
      'mystery_detail',
      'science_detail',
      'colour_detail',
      'funny_detail'
    ];

    for (
      const key of keys
    ) {
      if (
        answers[key] &&
        answers[key] !==
          'surprise'
      ) {
        return answers[key];
      }
    }

    return '';
  }

  function wildcardAnswer(answers) {
    return (
      answers.wildcard_a ||
      answers.wildcard_b ||
      answers.wildcard_c ||
      ''
    );
  }

  function scoreRecord(
    record,
    input,
    variant = 0
  ) {
    const answers =
      normaliseAnswers(input);

    const matching =
      record.matching || {};

    const archetypes =
      recordArchetypes(
        record
      );

    let score = 20;

    const reasons = [];

    if (
      answers.occasion &&
      answers.occasion !==
        'any' &&
      matching.occasions
        ?.includes(
          answers.occasion
        )
    ) {
      score += 8;

      reasons.push(
        `It works for ${questionLabel(
          'occasion',
          answers.occasion
        ).toLowerCase()}.`
      );
    }

    const matchedMoods =
      (
        answers.moods ||
        []
      ).filter(
        mood =>
          matching.moods
            ?.includes(
              mood
            )
      );

    score +=
      matchedMoods.length *
      6;

    if (
      matchedMoods.length
    ) {
      reasons.push(
        `It matches your ${humanList(
          matchedMoods
            .map(
              mood =>
                questionLabel(
                  'moods',
                  mood
                ).toLowerCase()
            )
        )} direction.`
      );
    }

    let worldMatches =
      0;

    for (
      const world of
      answers.worlds ||
      []
    ) {
      const desired =
        WORLD_ARCHETYPES[
          world
        ] || [];

      if (
        desired.some(
          archetype =>
            archetypes.includes(
              archetype
            )
        )
      ) {
        worldMatches++;

        score += 9;
      }
    }

    if (worldMatches) {
      reasons.push(
        'It fits the Halloween world you picked.'
      );
    }

    const detail =
      branchAnswer(
        answers
      );

    if (detail) {
      const desired =
        DETAIL_ARCHETYPES[
          detail
        ] || [];

      if (
        desired.some(
          archetype =>
            archetypes.includes(
              archetype
            )
        )
      ) {
        score += 12;

        reasons.push(
          'It follows the character direction you chose.'
        );
      }
    }

    const owned =
      ownedIdsForAnswers(
        answers
      );

    const compatibleOwned =
      owned.filter(
        id =>
          ownedCompatible(
            record,
            id
          )
      );

    score +=
      compatibleOwned.length *
      5;

    if (
      compatibleOwned.length
    ) {
      reasons.push(
        `It can use ${humanList(
          compatibleOwned
            .slice(0, 3)
            .map(
              id =>
                OWNED_DEFS[id]
                  ?.label
            )
        )} you already have.`
      );
    }

    const finishingTouches =
      finishValuesForAnswers(
        answers
      );

    const matchedFinish =
      finishingTouches.filter(
        value =>
          finishCompatible(
            record,
            value
          )
      );

    score +=
      matchedFinish.length *
      4;

    if (
      answers.effort ===
      'quick'
    ) {
      if (
        matching.min_effort ===
        'quick'
      ) {
        score += 7;

        reasons.push(
          'It suits the quick-prep route you asked for.'
        );
      } else {
        score -= 3;
      }
    }

    if (
      answers.effort ===
      'creative'
    ) {
      score +=
        matching.min_effort ===
        'creative'
          ? 4
          : 2;
    }

    if (
      [
        'zero',
        'one_to_10'
      ].includes(
        answers.budget
      )
    ) {
      if (
        matching.min_effort ===
        'quick'
      ) {
        score += 5;
      }

      if (
        compatibleOwned.length
      ) {
        score += 4;
      }
    }

    const practical =
      answers.practical ||
      [];

    if (
      practical.includes(
        'warm'
      ) &&
      matching.wardrobes
        ?.includes(
          'layers'
        )
    ) {
      score += 5;
    }

    if (
      practical.includes(
        'outdoors'
      ) &&
      matching.wardrobes
        ?.some(
          value =>
            [
              'layers',
              'denim',
              'casual_basics'
            ].includes(value)
        )
    ) {
      score += 3;
    }

    if (
      practical.includes(
        'sit_down'
      ) &&
      matching.wardrobes
        ?.some(
          value =>
            [
              'layers',
              'denim',
              'casual_basics',
              'shirt_trousers'
            ].includes(value)
        )
    ) {
      score += 3;
    }

    if (
      practical.includes(
        'hands_free'
      )
    ) {
      const text =
        costumeText(
          record
        );

      if (
        !/carry|holding|hold a|wand|lantern|trident|binocular|torch|detector/.test(
          text
        )
      ) {
        score += 3;
      }
    }

    if (
      practical.includes(
        'work_school'
      )
    ) {
      if (
        matching.occasions
          ?.includes('work') ||
        matching.occasions
          ?.includes('school')
      ) {
        score += 4;
      }
    }

    const wildcard =
      wildcardAnswer(
        answers
      );

    if (wildcard) {
      const desired =
        WILDCARD_ARCHETYPES[
          wildcard
        ] || [];

      if (
        desired.some(
          archetype =>
            archetypes.includes(
              archetype
            )
        )
      ) {
        score += 2;
      }
    }

    const id =
      costumeNumber(
        record
      );

    score +=
      (
        (
          id * 7 +
          variant * 11
        ) %
        9
      ) /
      10;

    return {
      score,
      reasons,
      compatibleOwned,
      archetypes
    };
  }

  function rankedForRole(
    data,
    role,
    answers,
    variant,
    used = new Set(),
    preferredArchetypes = []
  ) {
    let pool =
      data.costumes.filter(
        record =>
          !used.has(
            record.costume_id
          ) &&
          roleCompatible(
            record,
            role,
            false
          )
      );

    if (
      !pool.length &&
      isChildRole(role)
    ) {
      pool =
        data.costumes.filter(
          record =>
            !used.has(
              record.costume_id
            ) &&
            roleCompatible(
              record,
              role,
              true
            )
        );
    }

    if (!pool.length) {
      pool =
        data.costumes.filter(
          record =>
            !used.has(
              record.costume_id
            ) &&
            record.active !==
              false
        );
    }

    return pool
      .map(
        record => {
          const scoring =
            scoreRecord(
              record,
              answers,
              variant
            );

          const coordinationBonus =
            preferredArchetypes.length &&
            scoring.archetypes.some(
              archetype =>
                preferredArchetypes.includes(
                  archetype
                )
            )
              ? 7
              : 0;

          return {
            record,
            score:
              scoring.score +
              coordinationBonus,
            reasons:
              scoring.reasons,
            compatibleOwned:
              scoring.compatibleOwned,
            archetypes:
              scoring.archetypes
          };
        }
      )
      .sort(
        (a, b) =>
          b.score -
          a.score
      );
  }

  function weightedTopPick(
    list,
    size = 5
  ) {
    if (!list.length) {
      return null;
    }

    const pool =
      list.slice(
        0,
        Math.min(
          size,
          list.length
        )
      );

    const floor =
      Math.min(
        ...pool.map(
          item =>
            item.score
        )
      );

    const weighted =
      pool.map(
        item => ({
          item,
          weight:
            Math.max(
              1,
              item.score -
                floor +
                2
            )
        })
      );

    const total =
      weighted.reduce(
        (
          sum,
          entry
        ) =>
          sum +
          entry.weight,
        0
      );

    let cursor =
      random() *
      total;

    for (
      const entry of
      weighted
    ) {
      cursor -=
        entry.weight;

      if (
        cursor <= 0
      ) {
        return entry.item;
      }
    }

    return weighted[0]
      .item;
  }

  function pickSingle(
    data,
    answers,
    role,
    seen,
    variant
  ) {
    let ranked =
      rankedForRole(
        data,
        role,
        answers,
        variant
      );

    const unseen =
      ranked.filter(
        item =>
          !seen?.has(
            item.record
              .costume_id
          )
      );

    if (
      unseen.length
    ) {
      ranked =
        unseen;
    }

    const picked =
      weightedTopPick(
        ranked,
        5
      ) ||
      ranked[0];

    return {
      status:
        'ok',

      costume_ids: [
        picked.record
          .costume_id
      ],

      audience_roles: [
        role
      ],

      group_id:
        null,

      member_matches: [
        {
          costume_id:
            picked.record
              .costume_id,

          reasons:
            picked.reasons,

          score:
            picked.score
        }
      ]
    };
  }

  function assignExistingGroup(
    data,
    group,
    roles,
    answers,
    variant
  ) {
    const byId =
      new Map(
        data.costumes.map(
          record => [
            record.costume_id,
            record
          ]
        )
      );

    const order =
      roles
        .map(
          (
            role,
            index
          ) => ({
            role,
            index
          })
        )
        .sort(
          (a, b) => {
            const aChild =
              isChildRole(
                a.role
              )
                ? 0
                : 1;

            const bChild =
              isChildRole(
                b.role
              )
                ? 0
                : 1;

            return (
              aChild -
              bChild
            );
          }
        );

    const used =
      new Set();

    const selected =
      new Array(
        roles.length
      );

    const matches =
      new Array(
        roles.length
      );

    for (
      const slot of order
    ) {
      let options =
        group.costume_ids
          .map(
            id =>
              byId.get(
                id
              )
          )
          .filter(Boolean)
          .filter(
            record =>
              !used.has(
                record.costume_id
              ) &&
              roleCompatible(
                record,
                slot.role,
                false
              )
          );

      if (
        !options.length &&
        isChildRole(
          slot.role
        )
      ) {
        options =
          group.costume_ids
            .map(
              id =>
                byId.get(
                  id
                )
            )
            .filter(Boolean)
            .filter(
              record =>
                !used.has(
                  record.costume_id
                ) &&
                roleCompatible(
                  record,
                  slot.role,
                  true
                )
            );
      }

      if (
        !options.length
      ) {
        return null;
      }

      const ranked =
        options
          .map(
            record => {
              const scoring =
                scoreRecord(
                  record,
                  answers,
                  variant
                );

              return {
                record,
                ...scoring
              };
            }
          )
          .sort(
            (a, b) =>
              b.score -
              a.score
          );

      const chosen =
        ranked[0];

      selected[
        slot.index
      ] =
        chosen.record;

      matches[
        slot.index
      ] = {
        costume_id:
          chosen.record
            .costume_id,

        reasons:
          chosen.reasons,

        score:
          chosen.score
      };

      used.add(
        chosen.record
          .costume_id
      );
    }

    return {
      group,
      records:
        selected,
      member_matches:
        matches,
      score:
        matches.reduce(
          (
            sum,
            match
          ) =>
            sum +
            match.score,
          0
        )
    };
  }

  function preferredCoordinationArchetypes(
    answers
  ) {
    const worlds =
      answers.worlds ||
      [];

    if (!worlds.length) {
      return [];
    }

    const combined =
      worlds.flatMap(
        world =>
          WORLD_ARCHETYPES[
            world
          ] || []
      );

    return [
      ...new Set(
        combined
      )
    ];
  }

  function fallbackGroup(
    data,
    roles,
    answers,
    variant
  ) {
    const used =
      new Set();

    const records =
      new Array(
        roles.length
      );

    const matches =
      new Array(
        roles.length
      );

    let preferred =
      preferredCoordinationArchetypes(
        answers
      );

    const order =
      roles
        .map(
          (
            role,
            index
          ) => ({
            role,
            index
          })
        )
        .sort(
          (a, b) => {
            const aChild =
              isChildRole(
                a.role
              )
                ? 0
                : 1;

            const bChild =
              isChildRole(
                b.role
              )
                ? 0
                : 1;

            return (
              aChild -
              bChild
            );
          }
        );

    for (
      const slot of
      order
    ) {
      const ranked =
        rankedForRole(
          data,
          slot.role,
          answers,
          variant,
          used,
          preferred
        );

      const chosen =
        weightedTopPick(
          ranked,
          3
        ) ||
        ranked[0];

      if (!chosen) {
        continue;
      }

      records[
        slot.index
      ] =
        chosen.record;

      matches[
        slot.index
      ] = {
        costume_id:
          chosen.record
            .costume_id,

        reasons:
          chosen.reasons,

        score:
          chosen.score
      };

      used.add(
        chosen.record
          .costume_id
      );

      if (
        !preferred.length
      ) {
        preferred =
          chosen.archetypes;
      }
    }

    return {
      group:
        null,

      records:
        records.filter(
          Boolean
        ),

      member_matches:
        matches.filter(
          Boolean
        ),

      score:
        matches
          .filter(Boolean)
          .reduce(
            (
              sum,
              match
            ) =>
              sum +
              match.score,
            0
          )
    };
  }

  function pickGroup(
    data,
    answers,
    roles,
    variant
  ) {
    const candidates =
      (
        data.groups ||
        []
      )
        .map(
          group =>
            assignExistingGroup(
              data,
              group,
              roles,
              answers,
              variant
            )
        )
        .filter(Boolean)
        .sort(
          (a, b) =>
            b.score -
            a.score
        );

    let chosen =
      weightedTopPick(
        candidates,
        3
      );

    if (!chosen) {
      chosen =
        fallbackGroup(
          data,
          roles,
          answers,
          variant
        );
    }

    return {
      status:
        'ok',

      costume_ids:
        chosen.records.map(
          record =>
            record.costume_id
        ),

      audience_roles:
        roles,

      group_id:
        chosen.group
          ?.group_id ||
        chosen.group
          ?.id ||
        'custom',

      group_title:
        chosen.group
          ?.title ||
        'Your Halloween crew',

      group_intro:
        chosen.group
          ?.intro ||
        'A coordinated collection of characters chosen around your answers.',

      member_matches:
        chosen.member_matches
    };
  }

  function selectResult(
    data,
    input,
    seen,
    variant
  ) {
    const answers =
      normaliseAnswers(
        input
      );

    if (
      answers.audience ===
        'couple' ||
      answers.audience ===
        'family'
    ) {
      return pickGroup(
        data,
        answers,
        rolesForGroup(
          answers
        ),
        variant
      );
    }

    return pickSingle(
      data,
      answers,
      roleForSingle(
        answers
      ),
      seen,
      variant
    );
  }

  const WARDROBE_LABELS = {
    dark_dress:
      'a dark dress',

    colourful_dress:
      'a colourful or patterned dress',

    dark_basics:
      'dark everyday clothes',

    shirt_trousers:
      'a shirt and trousers',

    denim:
      'jeans or dungarees',

    layers:
      'a cardigan, jacket or coat',

    pale_outfit:
      'white or pale clothes',

    casual_basics:
      'comfortable casual clothes'
  };

  /*
   * More specific finishing touches come before the generic prop rule.
   */

  const EXTRA_REQUIREMENTS = [
    {
      key:
        'cape',
      label:
        'a cape',
      pattern:
        /cape/
    },

    {
      key:
        'hat',
      label:
        'a character hat',
      pattern:
        /hat|beret/
    },

    {
      key:
        'boots',
      label:
        'boots',
      pattern:
        /boot/
    },

    {
      key:
        'scarf',
      label:
        'a scarf',
      pattern:
        /scarf/
    },

    {
      key:
        'wand',
      label:
        'a simple wand',
      pattern:
        /wand/
    },

    {
      key:
        'crown',
      label:
        'a crown',
      pattern:
        /crown/
    },

    {
      key:
        'mask',
      label:
        'a mask',
      pattern:
        /mask/
    },

    {
      key:
        'wings',
      label:
        'wings',
      pattern:
        /wing/
    },

    {
      key:
        'book',
      label:
        'a book or notebook',
      pattern:
        /book|notebook/
    },

    {
      key:
        'map',
      label:
        'a map',
      pattern:
        /map/
    },

    {
      key:
        'apron',
      label:
        'an apron',
      pattern:
        /apron/
    },

    {
      key:
        'glasses',
      label:
        'glasses',
      pattern:
        /glasses/
    },

    {
      key:
        'waistcoat',
      label:
        'a waistcoat',
      pattern:
        /waistcoat|vest/
    },

    {
      key:
        'bag',
      label:
        'a little bag or satchel',
      pattern:
        /bag|satchel/
    },

    {
      key:
        'lantern',
      label:
        'a pretend lantern',
      pattern:
        /lantern/
    },

    {
      key:
        'horns',
      label:
        'little horns or a tail',
      pattern:
        /horn|tail/
    },

    {
      key:
        'torch',
      label:
        'a torch',
      pattern:
        /torch|flashlight/
    },

    {
      key:
        'detector',
      label:
        'a pretend detector',
      pattern:
        /ghost detector|detector|scanner|receiver/
    },

    {
      key:
        'backpack',
      label:
        'a backpack',
      pattern:
        /backpack|rucksack/
    },

    {
      key:
        'utility_vest',
      label:
        'a pocketed jacket or utility vest',
      pattern:
        /utility vest|pocketed jacket|pocketed vest/
    },

    {
      key:
        'prop',
      label:
        'a simple character prop',
      pattern:
        /trident|flask|binocular|magnifier|clipboard|key|envelope/
    }
  ];

  function deriveRequirements(record) {
    const text =
      costumeText(
        record
      );

    const base =
      (
        record.matching
          ?.wardrobes ||
        []
      )
        .map(
          wardrobe =>
            WARDROBE_LABELS[
              wardrobe
            ]
        )
        .filter(Boolean);

    const matched =
      EXTRA_REQUIREMENTS
        .filter(
          requirement =>
            requirement.pattern
              .test(text)
        );

    const specific =
      matched.filter(
        item =>
          item.key !==
          'prop'
      );

    const chosen =
      specific.length
        ? specific
        : matched;

    const seen =
      new Set();

    const extras =
      chosen
        .filter(
          item => {
            if (
              seen.has(
                item.key
              )
            ) {
              return false;
            }

            seen.add(
              item.key
            );

            return true;
          }
        )
        .map(
          requirement => ({
            key:
              requirement.key,

            label:
              requirement.label
          })
        );

    return {
      base:
        [
          ...new Set(base)
        ],

      extras
    };
  }

  function requirementIsOwned(
    requirement,
    ownedIds
  ) {
    if (
      ownedIds.includes(
        requirement.key
      )
    ) {
      return true;
    }

    const requirementText =
      requirement.label
        .toLowerCase();

    return ownedIds.some(
      id => {
        const definition =
          OWNED_DEFS[
            id
          ];

        if (!definition) {
          return false;
        }

        return (
          definition.label
            .toLowerCase()
            .split(' ')
            .some(
              word =>
                word.length >
                  4 &&
                requirementText.includes(
                  word
                )
            )
        );
      }
    );
  }

  function guideFor(
    record,
    input
  ) {
    const answers =
      normaliseAnswers(
        input
      );

    const ownedIds =
      ownedIdsForAnswers(
        answers
      );

    const compatibleOwned =
      ownedIds.filter(
        id =>
          ownedCompatible(
            record,
            id
          )
      );

    const requirements =
      deriveRequirements(
        record
      );

    const missingExtras =
      requirements.extras
        .filter(
          requirement =>
            !requirementIsOwned(
              requirement,
              ownedIds
            )
        );

    const ownedLabels =
      compatibleOwned
        .map(
          id =>
            OWNED_DEFS[id]
              ?.label
        )
        .filter(Boolean);

    let missing =
      missingExtras
        .slice(0, 3)
        .map(
          item =>
            item.label
        );

    if (
      !ownedLabels.length &&
      requirements.base.length
    ) {
      missing = [
        requirements.base[0],
        ...missing
      ].slice(
        0,
        3
      );
    }

    const noExtraNeeded =
      !missing.length;

    if (
      noExtraNeeded
    ) {
      missing = [
        'No extra piece is essential for this idea.'
      ];
    }

    let quick = '';

    if (
      ownedLabels.length
    ) {
      quick =
        `Start with ${humanList(
          ownedLabels.slice(
            0,
            2
          )
        )}. `;

      quick +=
        noExtraNeeded
          ? 'That is enough to make the idea work, so any extra details are completely optional.'
          : `Add ${missing[0]} to make the character easier to recognise.`;
    }

    else if (
      requirements.base
        .length
    ) {
      quick =
        `Start with ${requirements.base[0]}. `;

      quick +=
        noExtraNeeded
          ? 'Keep the rest simple and let the character idea do the work.'
          : `Add ${missing[0]} as the recognisable Halloween detail.`;
    }

    else {
      quick =
        record.matching
          ?.wardrobe_tip ||
        'Start with comfortable everyday clothes and add one detail that makes the character recognisable.';
    }

    return {
      ownedIds:
        compatibleOwned,

      ownedLabels,

      missing,

      noExtraNeeded,

      quick,

      fullTip:
        record.matching
          ?.wardrobe_tip ||
        ''
    };
  }

  function occasionStoryPhrase(value) {
    const phrases = {
      party:
        'a Halloween party',

      work:
        'a day at work',

      school:
        'a school day',

      trick_or_treat:
        'an evening of trick or treating',

      home:
        'a Halloween night at home with family',

      any:
        'whatever Halloween brings'
    };

    return (
      phrases[value] ||
      'Halloween'
    );
  }

  function personalisedStory(
    record,
    input,
    guide,
    route
  ) {
    if (
      route === 'shared' ||
      route === 'surprise'
    ) {
      return (
        record.display_result
          .story
      );
    }

    const answers =
      normaliseAnswers(
        input
      );

    const title =
      record.display_result
        .costume_title;

    const worlds =
      labelsFor(
        'worlds',
        answers.worlds
      );

    const opening = [
      `Your version of ${title} is getting ready for ${occasionStoryPhrase(
        answers.occasion
      )}.`,

      worlds.length
        ? `You steered the adventure towards ${humanList(
            worlds
              .slice(0, 2)
              .map(
                value =>
                  value.toLowerCase()
              )
          )}.`
        : '',

      guide.ownedLabels
        .length
        ? `You’ve already got ${humanList(
            guide.ownedLabels
              .slice(0, 2)
          )}, so the character has a head start.`
        : '',

      answers.moods
        .length
        ? `You wanted something ${humanList(
            labelsFor(
              'moods',
              answers.moods
            )
              .slice(0, 2)
              .map(
                value =>
                  value.toLowerCase()
              )
          )}, so make that the personality of your version.`
        : ''
    ]
      .filter(Boolean)
      .join(' ');

    let ending = '';

    if (
      answers.budget ===
      'zero'
    ) {
      ending =
        'For your real-life version, use what you already own and treat every extra as optional.';
    }

    else if (
      answers.effort ===
      'quick'
    ) {
      ending =
        'For your real-life version, keep the outfit simple and let one recognisable detail do the Halloween work.';
    }

    return [
      opening,
      record.display_result
        .story,
      ending
    ]
      .filter(Boolean)
      .join('\n\n');
  }

  function parseShared(
    data,
    search
  ) {
    const params =
      new URLSearchParams(
        search
      );

    const first =
      params.get(
        'costume'
      );

    if (!first) {
      return null;
    }

    const ids = [
      first,
      ...(
        params.get(
          'companions'
        ) ||
        ''
      )
        .split(',')
        .filter(Boolean)
    ];

    if (
      ids.length > 6 ||
      new Set(ids).size !==
        ids.length ||
      ids.some(
        id =>
          !/^H\d{3}$/.test(
            id
          ) ||
          !data.costumes
            .some(
              record =>
                record.costume_id ===
                id
            )
      )
    ) {
      return {
        invalid:
          true
      };
    }

    const groupId =
      params.get(
        'group'
      );

    if (
      ids.length > 1 &&
      groupId !==
        'custom' &&
      !(
        data.groups ||
        []
      ).some(
        group =>
          (
            group.group_id ||
            group.id
          ) ===
            groupId &&
          ids.every(
            id =>
              group.costume_ids
                .includes(id)
          )
      )
    ) {
      return {
        invalid:
          true
      };
    }

    return {
      status:
        'ok',

      costume_ids:
        ids,

      audience_roles:
        null,

      group_id:
        ids.length >
        1
          ? groupId ||
            'custom'
          : null,

      member_matches:
        []
    };
  }

  function resultURL(
    base,
    result
  ) {
    const url =
      new URL(base);

    [
      'costume',
      'companions',
      'group',
      'variant'
    ].forEach(
      key =>
        url.searchParams
          .delete(key)
    );

    url.searchParams
      .set(
        'costume',
        result
          .costume_ids[0]
      );

    url.searchParams
      .set(
        'variant',
        'adaptive'
      );

    if (
      result.costume_ids
        .length > 1
    ) {
      url.searchParams
        .set(
          'companions',
          result.costume_ids
            .slice(1)
            .join(',')
        );

      url.searchParams
        .set(
          'group',
          result.group_id ||
          'custom'
        );
    }

    return url.href;
  }

  const API = {
    VERSION,
    QUESTIONNAIRE_VERSION,
    CONSENT_VERSION,
    QUESTION_BANK,
    DEFAULTS,
    normaliseAnswers,
    journeyFor,
    scoreRecord,
    selectResult,
    guideFor,
    parseShared,
    resultURL
  };

  if (
    typeof module !==
      'undefined' &&
    module.exports
  ) {
    module.exports =
      API;
  }

  root.PopsyHalloween =
    API;

  if (
    !root.document ||
    !root.customElements ||
    root.customElements
      .get(
        'popsy-halloween-generator'
      )
  ) {
    return;
  }

  class PopsyGenerator
    extends HTMLElement {

    connectedCallback() {
      if (
        this._connected
      ) {
        return;
      }

      this._connected =
        true;

      this.app =
        this.querySelector(
          '[data-popsy-app]'
        );

      this.status =
        this.querySelector(
          '[data-popsy-status]'
        );

      this.answers =
        {};

      this.seen =
        new Set();

      this.step =
        0;

      this.route =
        'quiz';

      this.journeyVariant =
        Math.floor(
          random() *
          3000
        );

      this.sessionId =
        uuid();

      this.questionnaireId =
        null;

      this.questionnaireIndex =
        0;

      this.resultCount =
        0;

      this.researchConsent =
        false;

      this.researchStarted =
        false;

      this.eventQueue =
        Promise.resolve();

      this.researchEvents =
        new Set();

      this.preview =
        this.dataset
          .previewMode ===
          'true' ||
        !!root.Shopify
          ?.designMode;

      this.addEventListener(
        'click',
        event =>
          this.onClick(
            event
          )
      );

      this.addEventListener(
        'change',
        event =>
          this.onChange(
            event
          )
      );

      this.addEventListener(
        'submit',
        event =>
          event
            .preventDefault()
      );

      this.addEventListener(
        'toggle',
        event => {
          if (
            event.target
              .matches(
                '[data-story]'
              ) &&
            event.target
              .open
          ) {
            this.emitOnce(
              'story_opened',
              `story:${event.target.dataset.story}`
            );
          }
        },
        true
      );

      this.load();
    }

    async load() {
      try {
        const inline =
          this.querySelector(
            'script[data-popsy-catalogue]'
          );

        this.data =
          inline
            ? JSON.parse(
                inline.textContent
              )
            : await fetch(
                this.dataset
                  .catalogueUrl,
                {
                  credentials:
                    'omit',

                  cache:
                    'default'
                }
              ).then(
                response => {
                  if (
                    !response.ok
                  ) {
                    throw new Error(
                      'Catalogue unavailable'
                    );
                  }

                  return response
                    .json();
                }
              );

        if (
          !Array.isArray(
            this.data
              .costumes
          ) ||
          !this.data
            .costumes
            .length
        ) {
          throw new Error(
            'Catalogue is incomplete'
          );
        }

        this.byId =
          new Map(
            this.data
              .costumes
              .map(
                record => [
                  record
                    .costume_id,
                  record
                ]
              )
          );

        this.querySelector(
          '[data-popsy-loading]'
        )?.remove();

        const shared =
          parseShared(
            this.data,
            root.location
              .search
          );

        if (
          shared &&
          !shared.invalid
        ) {
          this.route =
            'shared';

          this.showResult(
            shared,
            false
          );
        }

        else {
          this.renderStart();

          if (
            shared?.invalid
          ) {
            this.say(
              'That shared result could not be opened, so we’ve started a fresh adventure instead.'
            );
          }
        }
      }

      catch {
        this.app
          .innerHTML = `
            <div class="phg-empty">

              <h2>
                Our costume cupboard has not opened just yet.
              </h2>

              <p>
                Please refresh the page and try again.
              </p>

              <button
                type="button"
                class="phg-button phg-button--primary"
                data-action="reload"
              >
                Try again
              </button>

            </div>
          `;
      }
    }

    say(text) {
      if (
        this.status
      ) {
        this.status
          .textContent =
          text;
      }
    }

    focusHeading() {
      const heading =
        this.app
          .querySelector(
            '[data-focus-heading]'
          );

      if (!heading) {
        return;
      }

      heading.focus({
        preventScroll:
          true
      });

      heading
        .scrollIntoView({
          behavior:
            root
              .matchMedia(
                '(prefers-reduced-motion: reduce)'
              )
              .matches
              ? 'auto'
              : 'smooth',

          block:
            'start'
        });
    }

    renderStart() {
      this.current =
        null;

      this.app
        .innerHTML = `
          <div class="phg-start">

            <p class="phg-kicker">
              YOUR HALLOWEEN ADVENTURE
            </p>

            <h2
              data-focus-heading
              tabindex="-1"
            >
              Let’s find a Halloween idea that actually fits you.
            </h2>

            <p>
              Your questions change as you go. We’ll use what you fancy, what you already own and how much effort you want to put in.
            </p>

            <div class="phg-start-actions">

              <button
                type="button"
                class="phg-button phg-button--primary"
                data-action="start"
              >
                Start my costume adventure
              </button>

              <button
                type="button"
                class="phg-button phg-button--secondary"
                data-action="surprise-start"
              >
                Surprise me
              </button>

            </div>

            <p class="phg-note">
              You’ll usually see around 10 questions, but the route changes depending on your answers.
            </p>

            ${this.researchBox()}

          </div>
        `;
    }

    renderSurprisePicker() {
      this.app
        .innerHTML = `
          <div class="phg-start">

            <h2
              data-focus-heading
              tabindex="-1"
            >
              Who is the surprise for?
            </h2>

            <p>
              Pick one and we’ll jump straight to a costume idea.
            </p>

            <div class="phg-start-actions">

              <button
                type="button"
                class="phg-button phg-button--secondary"
                data-action="surprise-audience"
                data-audience="woman"
              >
                Woman
              </button>

              <button
                type="button"
                class="phg-button phg-button--secondary"
                data-action="surprise-audience"
                data-audience="man"
              >
                Man
              </button>

            </div>

            <button
              type="button"
              class="phg-button phg-button--quiet"
              data-action="home"
            >
              Back
            </button>

          </div>
        `;

      this.focusHeading();
    }

    startQuiz() {
      this.route =
        'quiz';

      this.answers =
        {};

      this.step =
        0;

      this.journeyVariant =
        Math.floor(
          random() *
          3000
        );

      this.questionnaireId =
        null;

      this.beginResearch();

      this.analytics(
        'generator_started'
      );

      this.renderQuestion();

      this.focusHeading();
    }

    journey() {
      return journeyFor(
        this.answers,
        this.journeyVariant
      );
    }

    currentQuestion() {
      const keys =
        this.journey();

      return QUESTION_BANK[
        keys[
          this.step
        ]
      ];
    }

    expectedSteps() {
      return Math.max(
        1,
        this.journey()
          .length
      );
    }

    renderCompositionQuestion() {
      const answers =
        normaliseAnswers(
          this.answers
        );

      return `
        <div class="phg-family-grid">

          <label>
            Total people

            <select
              class="phg-select"
              data-group-size
            >
              ${[
                3,
                4,
                5,
                6
              ]
                .map(
                  number => `
                    <option
                      value="${number}"
                      ${
                        answers.group_size ===
                        number
                          ? 'selected'
                          : ''
                      }
                    >
                      ${number}
                    </option>
                  `
                )
                .join('')}
            </select>

          </label>

          <label>
            Adult women

            <select
              class="phg-select"
              data-group-women-count
            >
              ${this.numberOptions(
                answers
                  .group_women_count
              )}
            </select>

          </label>

          <label>
            Adult men

            <select
              class="phg-select"
              data-group-men-count
            >
              ${this.numberOptions(
                answers
                  .group_men_count
              )}
            </select>

          </label>

          <label>
            Girls

            <select
              class="phg-select"
              data-group-girls-count
            >
              ${this.numberOptions(
                answers
                  .group_girls_count
              )}
            </select>

          </label>

          <label>
            Boys

            <select
              class="phg-select"
              data-group-boys-count
            >
              ${this.numberOptions(
                answers
                  .group_boys_count
              )}
            </select>

          </label>

        </div>
      `;
    }

    numberOptions(
      selected
    ) {
      return [
        0,
        1,
        2,
        3,
        4,
        5,
        6
      ]
        .map(
          number => `
            <option
              value="${number}"
              ${
                Number(
                  selected
                ) === number
                  ? 'selected'
                  : ''
              }
            >
              ${number}
            </option>
          `
        )
        .join('');
    }

    renderQuestion() {
      const question =
        this
          .currentQuestion();

      if (!question) {
        this.generate(
          false
        );

        return;
      }

      const estimate =
        this.expectedSteps();

      const progress =
        Math.min(
          100,
          (
            (
              this.step +
              1
            ) /
            estimate
          ) *
          100
        );

      let body = '';

      if (
        question.type ===
        'composition'
      ) {
        body =
          this.renderCompositionQuestion();
      }

      else {
        const selected =
          this.answers[
            question.key
          ];

        const isMulti =
          question.type ===
          'multi';

        const selectedValues =
          Array.isArray(
            selected
          )
            ? selected
            : [];

        body = `
          <fieldset
            class="phg-options ${
              isMulti
                ? 'phg-options--multi'
                : ''
            }"
          >

            <legend class="phg-sr-only">
              ${esc(
                question.title
              )}
            </legend>

            ${question.options
              .map(
                option => {
                  const checked =
                    isMulti
                      ? selectedValues
                          .includes(
                            option.value
                          )
                      : selected ===
                        option.value;

                  return `
                    <label class="phg-option">

                      <input
                        type="${
                          isMulti
                            ? 'checkbox'
                            : 'radio'
                        }"
                        name="${esc(
                          question.key
                        )}"
                        value="${esc(
                          option.value
                        )}"
                        data-question-key="${esc(
                          question.key
                        )}"
                        ${
                          isMulti
                            ? `data-max="${question.max || 99}"`
                            : ''
                        }
                        ${
                          checked
                            ? 'checked'
                            : ''
                        }
                      >

                      <span class="phg-option-content">

                        <span class="phg-option-title">
                          ${esc(
                            option.label
                          )}
                        </span>

                        ${
                          option.description
                            ? `
                              <span class="phg-option-description">
                                ${esc(
                                  option.description
                                )}
                              </span>
                            `
                            : ''
                        }

                      </span>

                    </label>
                  `;
                }
              )
              .join('')}

          </fieldset>
        `;
      }

      this.app
        .innerHTML = `
          <form
            class="phg-quiz"
            novalidate
          >

            <div
              class="phg-progress"
              aria-label="Progress through your costume questions"
            >
              <span
                style="width:${progress}%"
              ></span>
            </div>

            <p class="phg-step-count">
              ${
                this.step ===
                0
                  ? 'Start here'
                  : `Question ${this.step + 1} of ${estimate}`
              }
            </p>

            <div class="phg-question">

              <h2
                data-focus-heading
                tabindex="-1"
              >
                ${esc(
                  question.title
                )}
              </h2>

              <p>
                ${esc(
                  question.hint
                )}
              </p>

            </div>

            ${body}

            <div class="phg-quiz-nav">

              <button
                type="button"
                class="phg-button phg-button--quiet"
                data-action="back"
              >
                ${
                  this.step
                    ? 'Back'
                    : 'Back to the start'
                }
              </button>

              <button
                type="button"
                class="phg-button phg-button--primary"
                data-action="next"
              >
                ${
                  this.step ===
                  estimate - 1
                    ? 'Find my idea'
                    : 'Next'
                }
              </button>

            </div>

          </form>
        `;
    }

    validateCurrentQuestion() {
      const question =
        this
          .currentQuestion();

      if (!question) {
        return true;
      }

      if (
        question.type ===
        'composition'
      ) {
        const answers =
          normaliseAnswers(
            this.answers
          );

        const total =
          answers
            .group_women_count +
          answers
            .group_men_count +
          answers
            .group_girls_count +
          answers
            .group_boys_count;

        if (
          total !==
          answers.group_size
        ) {
          this.say(
            `Your group has ${answers.group_size} people. Make the four counts add up to ${answers.group_size}.`
          );

          return false;
        }

        return true;
      }

      const value =
        this.answers[
          question.key
        ];

      if (
        question.type ===
        'multi'
      ) {
        if (
          !Array.isArray(
            value
          ) ||
          !value.length
        ) {
          this.say(
            'Choose at least one answer to carry on.'
          );

          return false;
        }

        return true;
      }

      if (!value) {
        this.say(
          'Choose an answer to carry on.'
        );

        return false;
      }

      return true;
    }

    nextQuestion() {
      if (
        !this
          .validateCurrentQuestion()
      ) {
        return;
      }

      this.say('');

      const nextJourney =
        this.journey();

      if (
        this.step <
        nextJourney.length -
        1
      ) {
        this.step++;

        this.renderQuestion();

        this.focusHeading();

        return;
      }

      this.questionnaireId =
        uuid();

      this.questionnaireIndex++;

      this.emit(
        'questionnaire_completed',
        {
          result:
            null,

          generation_id:
            null
        }
      );

      this.generate(
        false
      );
    }

    generate(reset) {
      if (reset) {
        this.seen
          .clear();
      }

      const result =
        selectResult(
          this.data,
          this.answers,
          this.seen,
          this.journeyVariant +
            this.resultCount
        );

      this.showResult(
        result,
        true
      );
    }

    shareBase() {
      try {
        const url =
          new URL(
            this.dataset
              .shareBase
          );

        if (
          url.protocol ===
            'https:' &&
          url.pathname
            .startsWith(
              '/pages/'
            )
        ) {
          return url.href;
        }
      }

      catch {
        /* Use fallback below. */
      }

      return (
        root.location
          .protocol ===
        'https:'
          ? root.location
              .origin +
            root.location
              .pathname
          : 'https://www.popsyclothing.co.uk/pages/halloween-costume-generator'
      );
    }

    showResult(
      result,
      recordEvent
    ) {
      this.current =
        result;

      this.generationId =
        uuid();

      result.costume_ids
        .forEach(
          id =>
            this.seen
              .add(id)
        );

      if (
        recordEvent
      ) {
        const kind =
          this.resultCount ===
          0
            ? 'first_result_shown'
            : 'alternative_result_shown';

        this.resultCount++;

        this.emit(
          kind
        );
      }

      this.renderResult();

      if (
        root.location
          .protocol !==
        'file:'
      ) {
        try {
          root.history
            .replaceState(
              {},
              '',
              resultURL(
                root.location
                  .href,
                result
              )
            );
        }

        catch {
          /* Sharing URL is optional. */
        }
      }

      this.focusHeading();
    }

    matchReasonsFor(
      index,
      record
    ) {
      const saved =
        this.current
          ?.member_matches?.[
            index
          ]
          ?.reasons;

      if (
        Array.isArray(
          saved
        ) &&
        saved.length
      ) {
        return saved
          .slice(
            0,
            3
          );
      }

      if (
        this.route ===
        'quiz'
      ) {
        return scoreRecord(
          record,
          this.answers,
          this.journeyVariant
        )
          .reasons
          .slice(
            0,
            3
          );
      }

      return [
        'It’s a character with plenty of room to make the outfit your own.'
      ];
    }

    resultRole(
      index,
      record
    ) {
      const explicit =
        this.current
          ?.audience_roles?.[
            index
          ];

      if (explicit) {
        return explicit;
      }

      if (
        this.route ===
          'quiz' ||
        this.route ===
          'surprise'
      ) {
        const roles =
          rolesForGroup(
            normaliseAnswers(
              this.answers
            )
          );

        if (
          roles[index]
        ) {
          return roles[
            index
          ];
        }
      }

      return (
        sketchPresentation(
          record
        ) === 'male'
          ? 'male'
          : 'female'
      );
    }

    roleLabel(role) {
      return (
        {
          female:
            'woman',

          male:
            'man',

          girl:
            'girl',

          boy:
            'boy',

          child:
            'child',

          any:
            'person'
        }[role] ||
        'person'
      );
    }

    budgetInstruction() {
      const budget =
        normaliseAnswers(
          this.answers
        ).budget;

      const text = {
        zero:
          'Prioritise a version made entirely from clothes already owned. Any extra should be optional, homemade or easily omitted.',

        one_to_10:
          'Keep any suggested extras to roughly £10 in total.',

        eleven_to_20:
          'Keep any suggested extras to roughly £20 in total.',

        twentyone_to_40:
          'Keep any suggested extras to roughly £40 in total.',

        over_40:
          'There is some flexibility on extras, but keep the suggestions relevant rather than adding things unnecessarily.',

        not_sure:
          'Keep suggestions practical and give a simple low-cost version first.',

        prefer_not_say:
          'Keep suggestions practical and avoid assuming a spending level.'
      };

      return (
        text[budget] ||
        ''
      );
    }

    shoppingPrompt(
      record,
      index = 0
    ) {
      const answers =
        normaliseAnswers(
          this.answers
        );

      const role =
        this.resultRole(
          index,
          record
        );

      const guide =
        guideFor(
          record,
          answers
        );

      const ownedAll =
        ownedIdsForAnswers(
          answers
        )
          .map(
            id =>
              OWNED_DEFS[id]
                ?.label
          )
          .filter(Boolean);

      const usefulOwned =
        guide.ownedLabels
          .length
          ? guide.ownedLabels
          : ownedAll;

      const moods =
        labelsFor(
          'moods',
          answers.moods
        );

      const worlds =
        labelsFor(
          'worlds',
          answers.worlds
        );

      const missingText =
        guide.noExtraNeeded
          ? 'No essential extra is needed.'
          : humanList(
              guide.missing
            );

      return `
I used the Popsy Halloween Costume Idea Generator and got this result:

Costume idea: ${record.display_result.costume_title}
For: ${this.roleLabel(role)}

Costume idea:
${record.display_result.short_description}

The answers that matter for putting the outfit together are:

Occasion: ${questionLabel('occasion', answers.occasion || 'any')}
Style direction: ${worlds.length ? humanList(worlds) : 'Open to ideas'}
Mood: ${moods.length ? humanList(moods) : 'Open to ideas'}
Preparation: ${questionLabel('effort', answers.effort || 'any')}

Already owned:
${usefulOwned.length ? humanList(usefulOwned) : 'No useful item was selected'}

Useful finishing touches or missing pieces:
${missingText}

Saved costume guidance:
${guide.fullTip}

Please help finish THIS SPECIFIC Halloween costume.

Start with the clothes already owned and do not replace them unnecessarily.

Check Popsy Clothing for suitable current pieces where relevant.

Only cite or link to Popsy when you can verify the exact product or collection on popsyclothing.co.uk. If Popsy does not have a suitable item, suggest the generic type of clothing or accessory needed instead.

For every Popsy suggestion, use the exact current product or collection name and a verified Popsy URL. Do not invent product details, price, stock, fabric, sizing or availability.

${this.budgetInstruction()}

Keep the answer practical and concise in British English.

Result:
${resultURL(this.shareBase(), this.current)}
      `.trim();
    }

    aiServiceURL(
      service,
      prompt
    ) {
      const query =
        encodeURIComponent(
          prompt
        );

      if (
        service ===
        'google'
      ) {
        return (
          'https://www.google.com/search?udm=50&q=' +
          query
        );
      }

      if (
        service ===
        'chatgpt'
      ) {
        return (
          'https://chatgpt.com/?hints=search&q=' +
          query
        );
      }

      if (
        service ===
        'grok'
      ) {
        return (
          'https://grok.com/?q=' +
          query
        );
      }

      if (
        service ===
        'perplexity'
      ) {
        return (
          'https://www.perplexity.ai/search?q=' +
          query
        );
      }

      return '#';
    }

    aiServiceIcon(
      service
    ) {
      if (
        service ===
        'google'
      ) {
        return `
          <svg
            viewBox="0 0 32 32"
            aria-hidden="true"
          >
            <path
              d="M16 3C17.2 10.5 21.5 14.8 29 16C21.5 17.2 17.2 21.5 16 29C14.8 21.5 10.5 17.2 3 16C10.5 14.8 14.8 10.5 16 3Z"
              fill="currentColor"
            />
          </svg>
        `;
      }

      if (
        service ===
        'chatgpt'
      ) {
        return `
          <svg
            viewBox="0 0 32 32"
            aria-hidden="true"
          >
            <g
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="16" cy="9" r="5.5"/>
              <circle cx="22" cy="12.5" r="5.5"/>
              <circle cx="22" cy="19.5" r="5.5"/>
              <circle cx="16" cy="23" r="5.5"/>
              <circle cx="10" cy="19.5" r="5.5"/>
              <circle cx="10" cy="12.5" r="5.5"/>
            </g>
          </svg>
        `;
      }

      if (
        service ===
        'grok'
      ) {
        return `
          <svg
            viewBox="0 0 32 32"
            aria-hidden="true"
          >
            <rect
              x="4"
              y="4"
              width="24"
              height="24"
              rx="6"
              fill="currentColor"
            />
            <path
              d="M11 11h10l-7 10h8"
              fill="none"
              stroke="#fff"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        `;
      }

      return `
        <svg
          viewBox="0 0 32 32"
          aria-hidden="true"
        >
          <g
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M7 7l18 18"/>
            <path d="M25 7L7 25"/>
            <path d="M16 4v24"/>
            <path d="M4 16h24"/>
            <rect
              x="8"
              y="8"
              width="16"
              height="16"
            />
          </g>
        </svg>
      `;
    }

    aiServiceLinks(
      record,
      index = 0,
      compact = false
    ) {
      const prompt =
        this.shoppingPrompt(
          record,
          index
        );

      const services = [
        {
          key:
            'google',

          label:
            'Google AI'
        },

        {
          key:
            'chatgpt',

          label:
            'ChatGPT'
        },

        {
          key:
            'grok',

          label:
            'Grok'
        },

        {
          key:
            'perplexity',

          label:
            'Perplexity'
        }
      ];

      return `
        <div
          class="phg-ai-links ${
            compact
              ? 'phg-ai-links--compact'
              : ''
          }"
          aria-label="Find costume pieces with AI"
        >

          ${services
            .map(
              service => `
                <a
                  class="phg-ai-link phg-ai-link--${service.key}"
                  href="${esc(
                    this.aiServiceURL(
                      service.key,
                      prompt
                    )
                  )}"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-ai-service="${esc(service.label)}"
                  data-ai-mode="costume_shopping"
                  data-costume-id="${esc(record.costume_id)}"
                >

                  <span
                    class="phg-ai-link-icon"
                    aria-hidden="true"
                  >
                    ${this.aiServiceIcon(service.key)}
                  </span>

                  <span class="phg-ai-link-label">
                    ${esc(service.label)}
                  </span>

                </a>
              `
            )
            .join('')}

        </div>
      `;
    }

    aiHTML(
      record,
      index = 0,
      compact = false
    ) {
      if (
        this.dataset
          .externalAiEnabled ===
        'false'
      ) {
        return '';
      }

      if (compact) {
        return `
          <div class="phg-ai-shop phg-ai-shop--compact">

            <p class="phg-ai-shop-small-title">
              Find pieces for this idea with AI
            </p>

            ${this.aiServiceLinks(
              record,
              index,
              true
            )}

          </div>
        `;
      }

      return `
        <section class="phg-ai-shop">

          <p class="phg-kicker">
            HELP WITH THE FINISHING TOUCHES
          </p>

          <h3>
            Find pieces for this costume idea with AI
          </h3>

          <p>
            These links use your costume and the answers you gave us, including what you already own and what is still missing.
          </p>

          ${this.aiServiceLinks(
            record,
            index,
            false
          )}

          <p class="phg-note">
            The AI is asked to verify Popsy products before linking to them. If Popsy doesn’t have the right piece, it can suggest the generic clothing or accessory you need instead.
          </p>

        </section>
      `;
    }

    storyPrompt(
      record
    ) {
      const guide =
        guideFor(
          record,
          this.answers
        );

      const story =
        personalisedStory(
          record,
          this.answers,
          guide,
          this.route
        );

      return `
I found the ${record.display_result.costume_title} using the Popsy Halloween Costume Idea Generator.

Here is my personalised character story:

${story}

Expand this into a playful Halloween character story of about 150 words. Keep the original character and costume idea. Keep it family friendly and in natural British English. Do not claim the illustration contains anything that is not visible.

Result:
${resultURL(this.shareBase(), this.current)}
      `.trim();
    }

    matchBox(
      reasons
    ) {
      return `
        <div class="phg-match-box">

          <h3>
            Why we picked this
          </h3>

          <ul>
            ${reasons
              .slice(
                0,
                3
              )
              .map(
                reason => `
                  <li>
                    ${esc(reason)}
                  </li>
                `
              )
              .join('')}
          </ul>

        </div>
      `;
    }

    guideHTML(
      record,
      guide
    ) {
      return `
        <section class="phg-make-guide">

          <h3>
            How to make this costume idea
          </h3>

          <div class="phg-guide-grid">

            <div class="phg-guide-card">

              <p class="phg-guide-label">
                USE WHAT YOU OWN
              </p>

              <p>
                ${
                  guide.ownedLabels
                    .length
                    ? esc(
                        humanList(
                          guide.ownedLabels
                        )
                      )
                    : 'Nothing you selected is essential for this idea, so start with the closest comfortable clothes you already have.'
                }
              </p>

            </div>

            <div class="phg-guide-card">

              <p class="phg-guide-label">
                FINISHING TOUCHES
              </p>

              <p>
                ${esc(
                  guide.noExtraNeeded
                    ? 'No extra piece is essential. Add something only if it makes the character more fun for you.'
                    : humanList(
                        guide.missing
                      )
                )}
              </p>

            </div>

            <div class="phg-guide-card">

              <p class="phg-guide-label">
                QUICK VERSION
              </p>

              <p>
                ${esc(
                  guide.quick
                )}
              </p>

            </div>

          </div>

          ${
            guide.fullTip
              ? `
                <details class="phg-tips">

                  <summary>
                    Full outfit idea
                  </summary>

                  <p>
                    ${esc(
                      guide.fullTip
                    )}
                  </p>

                </details>
              `
              : ''
          }

        </section>
      `;
    }

    shareHTML() {
      return `
        <div class="phg-share-block">

          <p class="phg-share-title">
            Share your idea
          </p>

          <div
            class="phg-share-buttons"
            role="group"
            aria-label="Share your Halloween idea"
          >

            <button
              type="button"
              class="phg-share-button"
              data-action="share-facebook"
              aria-label="Share on Facebook"
            >

              <span
                class="phg-share-icon"
                aria-hidden="true"
              >
                <svg viewBox="0 0 24 24">
                  <path
                    d="M13.5 22v-9h3l.5-3h-3.5V8.1c0-.9.3-1.6 1.7-1.6H17V3.8c-.3 0-1.4-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3v2H7.5v3h2.8v9h3.2z"
                  />
                </svg>
              </span>

              <span>
                Facebook
              </span>

            </button>

            <button
              type="button"
              class="phg-share-button"
              data-action="share-instagram"
              aria-label="Share on Instagram"
            >

              <span
                class="phg-share-icon"
                aria-hidden="true"
              >
                <svg viewBox="0 0 24 24">
                  <rect
                    x="3"
                    y="3"
                    width="18"
                    height="18"
                    rx="5"
                    ry="5"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  />

                  <circle
                    cx="12"
                    cy="12"
                    r="4"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  />

                  <circle
                    cx="17.5"
                    cy="6.5"
                    r="1.3"
                    fill="currentColor"
                  />
                </svg>
              </span>

              <span>
                Instagram
              </span>

            </button>

            <button
              type="button"
              class="phg-share-button"
              data-action="share-x"
              aria-label="Share on X"
            >

              <span
                class="phg-share-icon"
                aria-hidden="true"
              >
                <svg viewBox="0 0 24 24">
                  <path
                    d="M5 4l14 16M19 4L5 20"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.2"
                    stroke-linecap="round"
                  />
                </svg>
              </span>

              <span>
                X
              </span>

            </button>

            <button
              type="button"
              class="phg-share-button"
              data-action="share-pinterest"
              aria-label="Share on Pinterest"
            >

              <span
                class="phg-share-icon"
                aria-hidden="true"
              >
                <svg viewBox="0 0 24 24">
                  <path
                    d="M12 3.5a8.5 8.5 0 0 0-3.1 16.4c-.1-1.4 0-3 .4-4.7l1.1-4.7s-.3-.7-.3-1.7c0-1.6.9-2.8 2.1-2.8 1 0 1.5.8 1.5 1.7 0 1-.6 2.5-.9 3.9-.3 1.2.6 2.1 1.8 2.1 2.2 0 3.7-2.8 3.7-6 0-2.5-2-4.5-5.1-4.5-3.7 0-6 2.8-6 5.8 0 1.1.3 1.9.8 2.5.2.2.2.3.1.6l-.3 1.1c-.1.4-.5.5-.9.3-1.7-.7-2.5-2.7-2.5-5 0-3.7 3.1-8.2 9.3-8.2 5 0 8.3 3.6 8.3 7.6 0 5.2-2.9 9.1-7.2 9.1-1.4 0-2.7-.8-3.2-1.7l-.9 3.4c-.3 1.2-1 2.8-1.5 3.7.9.3 1.9.5 2.9.5A8.5 8.5 0 0 0 12 3.5z"
                  />
                </svg>
              </span>

              <span>
                Pinterest
              </span>

            </button>

          </div>

          <button
            type="button"
            class="phg-button phg-button--quiet"
            data-action="copy-link"
          >
            Copy result link
          </button>

        </div>
      `;
    }

    renderResult() {
      const lead =
        this.byId.get(
          this.current
            .costume_ids[0]
        );

      const leadGuide =
        guideFor(
          lead,
          this.answers
        );

      const leadStory =
        personalisedStory(
          lead,
          this.answers,
          leadGuide,
          this.route
        );

      const leadRole =
        this.resultRole(
          0,
          lead
        );

      const leadChild =
        isChildRole(
          leadRole
        );

      const companions =
        this.current
          .costume_ids
          .slice(1)
          .map(
            (
              id,
              offset
            ) => ({
              record:
                this.byId
                  .get(id),

              index:
                offset +
                1
            })
          )
          .filter(
            item =>
              item.record
          );

      const isGroup =
        companions.length >
        0;

      this.app
        .innerHTML = `
          <section class="phg-result">

            <p class="phg-result-meta">
              ${
                isGroup
                  ? 'YOUR HALLOWEEN CREW'
                  : 'YOUR HALLOWEEN IDEA'
              }
            </p>

            <div class="phg-result-grid">

              <div class="phg-artwork">

                <img
                  src="${esc(
                    lead.assets
                      .preview_image_url
                  )}"
                  alt="${esc(
                    lead.assets
                      .image_alt_text
                  )}"
                  width="${
                    lead.assets
                      .width ||
                    1055
                  }"
                  height="${
                    lead.assets
                      .height ||
                    1491
                  }"
                  fetchpriority="high"
                  decoding="async"
                >

              </div>

              <div class="phg-result-copy">

                <h2
                  class="phg-result-title"
                  data-focus-heading
                  tabindex="-1"
                >
                  ${esc(
                    lead
                      .display_result
                      .costume_title
                  )}
                </h2>

                <p class="phg-result-intro">
                  ${esc(
                    lead
                      .display_result
                      .short_description
                  )}
                </p>

                ${this.matchBox(
                  this.matchReasonsFor(
                    0,
                    lead
                  )
                )}

                ${
                  leadChild
                    ? `
                      <p class="phg-note">
                        This idea is being adapted for a child. The master picture is character inspiration, while the outfit guidance is based on the child route you chose.
                      </p>
                    `
                    : ''
                }

                <details
                  class="phg-story"
                  data-story="${lead.costume_id}"
                >

                  <summary>
                    Read my character story
                  </summary>

                  ${leadStory
                    .split(
                      '\n\n'
                    )
                    .map(
                      paragraph => `
                        <p>
                          ${esc(
                            paragraph
                          )}
                        </p>
                      `
                    )
                    .join('')}

                </details>

                ${this.guideHTML(
                  lead,
                  leadGuide
                )}

                <div class="phg-actions">

                  <button
                    class="phg-button phg-button--primary"
                    type="button"
                    data-action="print"
                  >
                    Print colouring page
                  </button>

                  <button
                    class="phg-button phg-button--secondary"
                    type="button"
                    data-action="download"
                    data-id="${lead.costume_id}"
                  >
                    Download picture
                  </button>

                </div>

                ${this.shareHTML()}

                <label class="phg-checkbox">

                  <input
                    type="checkbox"
                    data-include-story
                  >

                  <span>
                    Include the story when printing
                  </span>

                </label>

              </div>

            </div>

            ${this.aiHTML(
              lead,
              0,
              false
            )}

            ${
              companions.length
                ? `
                  <section class="phg-companions">

                    <h3>
                      ${esc(
                        this.current
                          .group_title ||
                        'Your matching costume ideas'
                      )}
                    </h3>

                    <p>
                      ${esc(
                        this.current
                          .group_intro ||
                        'A few characters chosen to work together.'
                      )}
                    </p>

                    <div class="phg-companion-grid">

                      ${companions
                        .map(
                          item => {
                            const record =
                              item.record;

                            const guide =
                              guideFor(
                                record,
                                this.answers
                              );

                            const role =
                              this.resultRole(
                                item.index,
                                record
                              );

                            return `
                              <article class="phg-companion-card">

                                <img
                                  src="${esc(
                                    record.assets
                                      .preview_image_url
                                  )}"
                                  alt="${esc(
                                    record.assets
                                      .image_alt_text
                                  )}"
                                  width="${
                                    record.assets
                                      .width ||
                                    1055
                                  }"
                                  height="${
                                    record.assets
                                      .height ||
                                    1491
                                  }"
                                  loading="lazy"
                                >

                                <h4>
                                  ${esc(
                                    record
                                      .display_result
                                      .costume_title
                                  )}
                                </h4>

                                <p>
                                  ${esc(
                                    record
                                      .display_result
                                      .short_description
                                  )}
                                </p>

                                ${
                                  isChildRole(
                                    role
                                  )
                                    ? `
                                      <p class="phg-note">
                                        Child-friendly adaptation
                                      </p>
                                    `
                                    : ''
                                }

                                ${this.matchBox(
                                  this.matchReasonsFor(
                                    item.index,
                                    record
                                  )
                                )}

                                ${this.guideHTML(
                                  record,
                                  guide
                                )}

                                ${this.aiHTML(
                                  record,
                                  item.index,
                                  true
                                )}

                                <button
                                  class="phg-button phg-button--quiet"
                                  type="button"
                                  data-action="download"
                                  data-id="${record.costume_id}"
                                >
                                  Download picture
                                </button>

                              </article>
                            `;
                          }
                        )
                        .join('')}

                    </div>

                  </section>
                `
                : ''
            }

            <details class="phg-ai-tools">

              <summary>
                Take the character story further
              </summary>

              <p>
                This optional prompt uses the character and your result, rather than shopping suggestions.
              </p>

              <textarea
                class="phg-ai-prompt"
                data-story-prompt
                rows="7"
                readonly
              >${esc(
                this.storyPrompt(
                  lead
                )
              )}</textarea>

              <button
                type="button"
                class="phg-button phg-button--secondary"
                data-action="copy-story-prompt"
              >
                Copy story prompt
              </button>

            </details>

            <div class="phg-restart">

              <button
                type="button"
                class="phg-button phg-button--primary"
                data-action="another"
              >
                Show me another idea
              </button>

              <button
                type="button"
                class="phg-button phg-button--quiet"
                data-action="edit-answers"
              >
                Change my answers
              </button>

            </div>

            <div class="phg-feedback">

              <p>
                Does this idea feel like a good match?
              </p>

              <button
                type="button"
                class="phg-button phg-button--secondary"
                data-action="feedback"
                data-feedback="like"
              >
                Yes, I like it
              </button>

              <button
                type="button"
                class="phg-button phg-button--quiet"
                data-action="feedback"
                data-feedback="not_for_me"
              >
                Not quite
              </button>

              <p
                data-feedback-status
                aria-live="polite"
              ></p>

            </div>

            ${this.researchBox()}

            ${this.researchProfileHTML()}

          </section>
        `;

      const collection =
        this.querySelector(
          '[data-popsy-collection]'
        );

      if (collection) {
        collection.hidden =
          leadChild ||
          !lead.matching
            ?.show_popsy_dress_suggestions;
      }
    }

    onChange(event) {
      const target =
        event.target;

      if (
        target.matches(
          '[data-group-size]'
        )
      ) {
        this.answers
          .group_size =
          Number(
            target.value
          );

        return;
      }

      if (
        target.matches(
          '[data-group-women-count]'
        )
      ) {
        this.answers
          .group_women_count =
          Number(
            target.value
          );

        return;
      }

      if (
        target.matches(
          '[data-group-men-count]'
        )
      ) {
        this.answers
          .group_men_count =
          Number(
            target.value
          );

        return;
      }

      if (
        target.matches(
          '[data-group-girls-count]'
        )
      ) {
        this.answers
          .group_girls_count =
          Number(
            target.value
          );

        return;
      }

      if (
        target.matches(
          '[data-group-boys-count]'
        )
      ) {
        this.answers
          .group_boys_count =
          Number(
            target.value
          );

        return;
      }

      const key =
        target.dataset
          .questionKey;

      if (!key) {
        if (
          target.matches(
            '[data-research-consent]'
          )
        ) {
          this.researchConsent =
            target.checked;

          if (
            target.checked
          ) {
            this.beginResearch();

            if (
              this.current &&
              !this.querySelector(
                '.phg-research-panel'
              )
            ) {
              this.querySelector(
                '.phg-result'
              )
                ?.insertAdjacentHTML(
                  'beforeend',
                  this.researchProfileHTML()
                );
            }
          }
        }

        return;
      }

      const question =
        QUESTION_BANK[
          key
        ];

      if (!question) {
        return;
      }

      if (
        question.type ===
        'single'
      ) {
        this.answers[
          key
        ] =
          target.value;

        if (
          key ===
            'audience' &&
          target.value ===
            'family'
        ) {
          this.answers
            .group_size =
            3;

          this.answers
            .group_women_count =
            1;

          this.answers
            .group_men_count =
            1;

          this.answers
            .group_girls_count =
            1;

          this.answers
            .group_boys_count =
            0;
        }

        return;
      }

      if (
        question.type ===
        'multi'
      ) {
        let values =
          Array.isArray(
            this.answers[
              key
            ]
          )
            ? [
                ...this.answers[
                  key
                ]
              ]
            : [];

        if (
          target.checked
        ) {
          if (
            target.value ===
            'none'
          ) {
            values = [
              'none'
            ];

            this.app
              .querySelectorAll(
                `input[data-question-key="${key}"]`
              )
              .forEach(
                input => {
                  if (
                    input.value !==
                    'none'
                  ) {
                    input.checked =
                      false;
                  }
                }
              );
          }

          else {
            values =
              values.filter(
                value =>
                  value !==
                  'none'
              );

            const none =
              this.app
                .querySelector(
                  `input[data-question-key="${key}"][value="none"]`
                );

            if (none) {
              none.checked =
                false;
            }

            if (
              !values.includes(
                target.value
              )
            ) {
              values.push(
                target.value
              );
            }

            if (
              values.length >
              (
                question.max ||
                99
              )
            ) {
              target.checked =
                false;

              values =
                values.filter(
                  value =>
                    value !==
                    target.value
                );

              this.say(
                `Choose up to ${question.max} answers for this question.`
              );
            }

            else {
              this.say('');
            }
          }
        }

        else {
          values =
            values.filter(
              value =>
                value !==
                target.value
            );
        }

        this.answers[
          key
        ] =
          values;
      }
    }

    async onClick(event) {
      const aiLink =
        event.target
          .closest(
            '[data-ai-service]'
          );

      if (aiLink) {
        this.analytics(
          'external_ai_opened',
          {
            service:
              aiLink.dataset
                .aiService,

            task:
              aiLink.dataset
                .aiMode,

            costume_id:
              aiLink.dataset
                .costumeId
          }
        );
      }

      const button =
        event.target
          .closest(
            '[data-action]'
          );

      if (
        !button ||
        !this.contains(
          button
        )
      ) {
        return;
      }

      const action =
        button.dataset
          .action;

      if (
        action ===
        'reload'
      ) {
        this.load();

        return;
      }

      if (
        action ===
        'start'
      ) {
        this.startQuiz();

        return;
      }

      if (
        action ===
        'home'
      ) {
        this.renderStart();

        this.focusHeading();

        return;
      }

      if (
        action ===
        'surprise-start'
      ) {
        this.renderSurprisePicker();

        return;
      }

      if (
        action ===
        'surprise-audience'
      ) {
        this.route =
          'surprise';

        this.answers = {
          ...DEFAULTS,

          audience:
            button.dataset
              .audience,

          occasion:
            'any',

          worlds:
            [],

          moods:
            [],

          effort:
            'any',

          budget:
            'not_sure'
        };

        const result =
          selectResult(
            this.data,
            this.answers,
            this.seen,
            this.journeyVariant +
              this.resultCount
          );

        this.showResult(
          result,
          true
        );

        return;
      }

      if (
        action ===
        'next'
      ) {
        this.nextQuestion();

        return;
      }

      if (
        action ===
        'back'
      ) {
        if (
          this.step >
          0
        ) {
          this.step--;

          this.renderQuestion();

          this.focusHeading();
        }

        else {
          this.renderStart();

          this.focusHeading();
        }

        return;
      }

      if (
        action ===
        'edit-answers'
      ) {
        if (
          this.route !==
          'quiz'
        ) {
          this.startQuiz();
        }

        else {
          this.step =
            0;

          this.renderQuestion();

          this.focusHeading();
        }

        return;
      }

      if (
        action ===
        'another'
      ) {
        this.generate(
          false
        );

        return;
      }

      if (
        action ===
        'copy-link'
      ) {
        await this.copyText(
          resultURL(
            this.shareBase(),
            this.current
          ),
          'Your Halloween idea link has been copied.'
        );

        this.emit(
          'share_intent',
          {
            platform:
              'copy_link'
          }
        );

        return;
      }

      if (
        action.startsWith(
          'share-'
        )
      ) {
        await this.sharePlatform(
          action.replace(
            'share-',
            ''
          )
        );

        return;
      }

      if (
        action ===
        'copy-story-prompt'
      ) {
        const box =
          this.querySelector(
            '[data-story-prompt]'
          );

        if (box) {
          await this.copyText(
            box.value,
            'Your character prompt has been copied.'
          );
        }

        return;
      }

      if (
        action ===
        'download'
      ) {
        await this.download(
          button.dataset
            .id,
          button
        );

        return;
      }

      if (
        action ===
        'print'
      ) {
        await this.print();

        return;
      }

      if (
        action ===
        'feedback'
      ) {
        const saved =
          await this.emitOnce(
            'feedback_submitted',
            `feedback:${this.generationId}`,
            {
              feedback:
                button.dataset
                  .feedback
            }
          );

        const status =
          this.app
            .querySelector(
              '[data-feedback-status]'
            );

        if (status) {
          status.textContent =
            saved
              ? 'Thank you. Your feedback has been saved.'
              : 'Thank you.';
        }

        return;
      }

      if (
        action ===
        'save-research-profile'
      ) {
        const research =
          {};

        const region =
          this.querySelector(
            '[data-research-region]'
          )
            ?.value;

        const plan =
          this.querySelector(
            '[data-wardrobe-plan]'
          )
            ?.value;

        if (region) {
          research.region =
            region;
        }

        if (plan) {
          research.wardrobe_plan =
            plan;
        }

        const saved =
          await this.emit(
            'research_profile_submitted',
            {
              research
            }
          );

        const status =
          this.querySelector(
            '[data-profile-status]'
          );

        if (status) {
          status.textContent =
            saved
              ? 'Thank you. Your extra answers have been saved.'
              : 'These answers could not be saved just now.';
        }
      }
    }

    async copyText(
      text,
      success
    ) {
      try {
        if (
          !root.navigator
            .clipboard
            ?.writeText
        ) {
          throw new Error(
            'Clipboard unavailable'
          );
        }

        await root.navigator
          .clipboard
          .writeText(
            text
          );

        this.say(
          success
        );
      }

      catch {
        const textarea =
          document
            .createElement(
              'textarea'
            );

        textarea.className =
          'phg-ai-prompt';

        textarea.value =
          text;

        this.app
          .appendChild(
            textarea
          );

        textarea.focus();

        textarea.select();

        this.say(
          'Select and copy the text shown below.'
        );
      }
    }

    openShareWindow(url) {
      const windowRef =
        root.open(
          url,
          '_blank',
          'noopener,noreferrer,width=760,height=720'
        );

      if (windowRef) {
        windowRef.opener =
          null;
      }
    }

    async sharePlatform(platform) {
      const record =
        this.byId.get(
          this.current
            .costume_ids[0]
        );

      const url =
        resultURL(
          this.shareBase(),
          this.current
        );

      const caption =
        record.display_result
          .share_caption;

      this.emit(
        'share_intent',
        {
          platform
        }
      );

      if (
        platform ===
        'facebook'
      ) {
        this.openShareWindow(
          'https://www.facebook.com/sharer/sharer.php?u=' +
          encodeURIComponent(
            url
          )
        );

        return;
      }

      if (
        platform ===
        'x'
      ) {
        this.openShareWindow(
          'https://twitter.com/intent/tweet?text=' +
          encodeURIComponent(
            caption
          ) +
          '&url=' +
          encodeURIComponent(
            url
          )
        );

        return;
      }

      if (
        platform ===
        'pinterest'
      ) {
        this.openShareWindow(
          'https://www.pinterest.com/pin/create/button/?url=' +
          encodeURIComponent(
            url
          ) +
          '&media=' +
          encodeURIComponent(
            record.assets
              .preview_image_url
          ) +
          '&description=' +
          encodeURIComponent(
            caption
          )
        );

        return;
      }

      if (
        platform ===
        'instagram'
      ) {
        if (
          root.navigator
            .share
        ) {
          try {
            await root.navigator
              .share({
                title:
                  `${record.display_result.costume_title} | Popsy`,

                text:
                  caption,

                url
              });

            return;
          }

          catch (error) {
            if (
              error.name ===
              'AbortError'
            ) {
              return;
            }
          }
        }

        await this.copyText(
          `${caption}\n\n${url}`,
          'Your Instagram caption and result link have been copied.'
        );
      }
    }

    async download(
      id,
      button
    ) {
      const record =
        this.byId.get(
          id
        );

      if (!record) {
        return;
      }

      this.emit(
        'download_requested',
        {
          result: {
            costume_ids: [
              id
            ],

            group_id:
              null
          }
        }
      );

      button.disabled =
        true;

      try {
        const response =
          await fetch(
            record.assets
              .download_image_url,
            {
              credentials:
                'omit'
            }
          );

        if (
          !response.ok
        ) {
          throw new Error(
            'Image unavailable'
          );
        }

        const blob =
          await response
            .blob();

        const url =
          URL
            .createObjectURL(
              blob
            );

        const anchor =
          document
            .createElement(
              'a'
            );

        anchor.href =
          url;

        anchor.download =
          `${record.assets.filename_stem}.png`;

        document.body
          .appendChild(
            anchor
          );

        anchor.click();

        anchor.remove();

        setTimeout(
          () =>
            URL
              .revokeObjectURL(
                url
              ),
          30000
        );

        this.say(
          'Your picture download has been requested.'
        );
      }

      catch {
        this.say(
          'Your browser could not save the picture directly. Open the full-size picture and use your browser save option.'
        );
      }

      finally {
        button.disabled =
          false;
      }
    }

    async print() {
      this.emit(
        'print_requested'
      );

      const includeStory =
        !!this.querySelector(
          '[data-include-story]'
        )
          ?.checked;

      const records =
        this.current
          .costume_ids
          .map(
            id =>
              this.byId
                .get(id)
          )
          .filter(Boolean);

      const frame =
        document
          .createElement(
            'iframe'
          );

      frame.style
        .cssText =
        'position:fixed;left:-10000px;top:0;width:210mm;height:297mm;border:0;';

      document.body
        .appendChild(
          frame
        );

      const doc =
        frame
          .contentDocument;

      doc.open();

      doc.write(`
        <!doctype html>
        <html lang="en-GB">

          <head>

            <meta charset="utf-8">

            <style>
              @page {
                size: A4 portrait;
                margin: 12mm;
              }

              body {
                margin: 0;
                color: #111;
                background: #fff;
                font: 11pt Arial, sans-serif;
              }

              .sheet {
                page-break-after: always;
                text-align: center;
              }

              .sheet:last-child {
                page-break-after: auto;
              }

              h1 {
                font-size: 16pt;
              }

              img {
                width: 100%;
                height: ${
                  includeStory
                    ? '190'
                    : '237'
                }mm;
                object-fit: contain;
              }

              .story {
                text-align: left;
                font-size: 10pt;
                line-height: 1.4;
              }
            </style>

          </head>

          <body>

            ${records
              .map(
                record => {
                  const guide =
                    guideFor(
                      record,
                      this.answers
                    );

                  const story =
                    personalisedStory(
                      record,
                      this.answers,
                      guide,
                      this.route
                    );

                  return `
                    <section class="sheet">

                      <h1>
                        ${esc(
                          record.display_result.costume_title
                        )}
                      </h1>

                      <img
                        src="${esc(
                          record.assets.download_image_url
                        )}"
                        alt="${esc(
                          record.assets.image_alt_text
                        )}"
                      >

                      ${
                        includeStory
                          ? `
                            <p class="story">
                              ${esc(
                                story
                              )}
                            </p>
                          `
                          : ''
                      }

                      <p>
                        Popsy Halloween Costume Idea Generator
                      </p>

                    </section>
                  `;
                }
              )
              .join('')}

          </body>

        </html>
      `);

      doc.close();

      setTimeout(
        () => {
          try {
            frame
              .contentWindow
              .focus();

            frame
              .contentWindow
              .print();
          }

          finally {
            setTimeout(
              () =>
                frame.remove(),
              60000
            );
          }
        },
        1200
      );
    }

    get researchAvailable() {
      try {
        const url =
          new URL(
            this.dataset
              .researchEndpoint
          );

        return (
          this.dataset
            .researchEnabled ===
            'true' &&
          !this.preview &&
          url.protocol ===
            'https:'
        );
      }

      catch {
        return false;
      }
    }

    researchBox() {
      if (
        !this
          .researchAvailable
      ) {
        return '';
      }

      return `
        <div class="phg-research-consent">

          <label class="phg-checkbox">

            <input
              type="checkbox"
              data-research-consent
              ${
                this
                  .researchConsent
                  ? 'checked'
                  : ''
              }
            >

            <span>
              I’m 18 or over and I’m happy for Popsy to save my answers and how I use this generator for Halloween research. Taking part is optional.
            </span>

          </label>

          <p
            class="phg-research-status"
            data-research-status
            aria-live="polite"
          ></p>

        </div>
      `;
    }

    researchProfileHTML() {
      if (
        !this
          .researchAvailable ||
        !this
          .researchConsent
      ) {
        return '';
      }

      const options =
        list =>
          '<option value="">Choose an answer</option>' +
          list
            .map(
              option =>
                `<option value="${option.value}">${esc(option.label)}</option>`
            )
            .join('');

      return `
        <details class="phg-research-panel">

          <summary>
            Two optional research questions
          </summary>

          <label>
            Where do you live?

            <select
              class="phg-select"
              data-research-region
            >
              ${options(
                REGIONS
              )}
            </select>

          </label>

          <label>
            Where do you expect most of your costume to come from?

            <select
              class="phg-select"
              data-wardrobe-plan
            >
              ${options(
                WARDROBE_PLANS
              )}
            </select>

          </label>

          <button
            type="button"
            class="phg-button phg-button--secondary"
            data-action="save-research-profile"
          >
            Share these answers
          </button>

          <p
            data-profile-status
            aria-live="polite"
          ></p>

        </details>
      `;
    }

    beginResearch() {
      if (
        !this
          .researchConsent ||
        !this
          .researchAvailable ||
        this
          .researchStarted
      ) {
        return;
      }

      this.sessionId =
        uuid();

      this.researchStarted =
        true;

      this.emit(
        'research_session_started',
        {
          answers:
            null,

          answered_fields:
            [],

          result:
            null
        }
      );
    }

    emitOnce(
      type,
      key,
      extra
    ) {
      if (
        this.researchEvents
          .has(key)
      ) {
        return Promise
          .resolve(
            false
          );
      }

      this.researchEvents
        .add(key);

      return this.emit(
        type,
        extra
      );
    }

    answeredFields() {
      const answers =
        normaliseAnswers(
          this.answers
        );

      return Object.keys(
        answers
      )
        .filter(
          key => {
            const value =
              answers[
                key
              ];

            if (
              Array.isArray(
                value
              )
            ) {
              return (
                value.length >
                0
              );
            }

            return (
              value !== '' &&
              value !==
                null &&
              value !==
                undefined
            );
          }
        );
    }

    emit(
      type,
      extra = {}
    ) {
      this.analytics(
        type
      );

      if (
        !this
          .researchAvailable ||
        !this
          .researchConsent
      ) {
        return Promise
          .resolve(
            false
          );
      }

      const payload = {
        event_id:
          uuid(),

        session_id:
          this.sessionId,

        generation_id:
          this.generationId ||
          null,

        event_type:
          type,

        occurred_at:
          new Date()
            .toISOString(),

        catalogue_version:
          this.data
            .catalogue_version ||
          '2026-10-20-v2',

        algorithm_version:
          VERSION,

        questionnaire_version:
          QUESTIONNAIRE_VERSION,

        consent_version:
          CONSENT_VERSION,

        entry_route:
          this.route,

        questionnaire_id:
          this.questionnaireId,

        questionnaire_index:
          this.questionnaireIndex,

        answered_fields:
          this.answeredFields(),

        answers:
          this.route ===
          'quiz'
            ? normaliseAnswers(
                this.answers
              )
            : null,

        result:
          this.current
            ? {
                costume_ids:
                  this.current
                    .costume_ids,

                group_id:
                  this.current
                    .group_id,

                audience_roles:
                  this.current
                    .audience_roles ||
                  null
              }
            : null,

        ...extra
      };

      const send =
        async () => {
          try {
            const base =
              this.dataset
                .researchEndpoint
                .replace(
                  /\/$/,
                  ''
                );

            const endpoint =
              base.endsWith(
                '/v1/events'
              )
                ? base
                : `${base}/v1/events`;

            const response =
              await fetch(
                endpoint,
                {
                  method:
                    'POST',

                  credentials:
                    'omit',

                  headers: {
                    'Content-Type':
                      'application/json'
                  },

                  body:
                    JSON.stringify(
                      payload
                    ),

                  keepalive:
                    true
                }
              );

            return response.ok;
          }

          catch {
            return false;
          }
        };

      this.eventQueue =
        this.eventQueue
          .then(
            send,
            send
          );

      return this
        .eventQueue;
    }

    analytics(
      type,
      extra = {}
    ) {
      if (
        this.preview ||
        this.dataset
          .analyticsEnabled !==
          'true'
      ) {
        return;
      }

      try {
        if (
          root.Shopify
            ?.customerPrivacy
            ?.analyticsProcessingAllowed
            ?.() !==
            true ||
          typeof root.Shopify
            ?.analytics
            ?.publish !==
            'function'
        ) {
          return;
        }

        root.Shopify
          .analytics
          .publish(
            `popsy_halloween:${type}`,
            {
              costume_id:
                this.current
                  ?.costume_ids
                  ?.[0] ||
                null,

              entry_route:
                this.route,

              algorithm_version:
                VERSION,

              ...extra
            }
          );
      }

      catch {
        /* Analytics must never break the generator. */
      }
    }
  }

  root.customElements
    .define(
      'popsy-halloween-generator',
      PopsyGenerator
    );

})(
  typeof globalThis !==
    'undefined'
    ? globalThis
    : this
);